import BassEditor from './bass/BassEditor';
import { EditingContext } from '@/state/EditingContext';
import { makeSelectionClipboard, placeSelection, insertSelection } from '@/state/sectionEditing';
import { clipFadeLengths } from '@/audio/clipEdits';
import { findCopyBar } from '@/state/editingGrid';
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import '@/App.css';
import { ExpoStation } from '@/components/Expo/ExpoStation';
import { ExpoControls } from '@/components/Expo/ExpoControls';
import { ExpoListen } from '@/components/Expo/ExpoListen';
import { expoSession, buildExpoBeat, expoBars } from '@/state/expo';
import { TopNav } from '@/components/TopNav';
import { MainLayout } from '@/components/MainLayout';
import { StatusBar } from '@/components/StatusBar';
import { AutoArrangerModal } from '@/components/Modals/AutoArrangerModal';
import { ExportModal } from '@/components/Modals/ExportModal';
import { ProjectsModal } from '@/components/Modals/ProjectsModal';
import { SectionMarkerModal } from '@/components/Modals/SectionMarkerModal';
import { GuestExportModal } from '@/components/Modals/GuestExportModal';
import { AuthModal } from '@/components/Modals/AuthModal';
import { WorkshopExportModal } from '@/components/Modals/WorkshopExportModal';
import { FacilitatorDashboard } from '@/components/FacilitatorDashboard';
import { StudentDashboard } from '@/components/StudentDashboard';
import { ListenPage } from '@/components/ListenPage';
import { AdminPanel } from '@/components/AdminPanel';
import { ShowcasePlaylist } from '@/components/Admin/ShowcasePlaylist';
import { ClassProgramLiveView } from '@/components/ClassProgramLiveView';
import { ClassProgramProjector } from '@/components/ClassProgramProjector';
import { TakeHomePage } from '@/components/Class/TakeHomePage';
import { ResetPasswordPage } from '@/components/ResetPasswordPage';
import { PhoneGate } from '@/components/PhoneGate';
import { FamilySignupPage } from '@/components/FamilyList/FamilySignupPage';
import { BattleProjector } from '@/components/Battles/BattleProjector';
import { JudgePage } from '@/components/Battles/JudgePage';
import { currentScreenIsPhone } from '@/utils/deviceGate';
import { BARS_RANGE, INSTRUMENT_COLORS, LYRICS_MARKER_COLOR, MAX_TRACKS } from '@/utils/constants';
import { useUndoRedo } from '@/state/useUndoRedo';
import { useKeyboardShortcuts } from '@/state/useKeyboardShortcuts';
import { useAuth } from '@/state/useAuth';
import { snapToGrid } from '@/state/useSnapGrid';
import { runAutoArrange } from '@/state/autoArranger';
import { clearPendingImport, parseSP2Payload, payloadToTracks, hasSP2Import } from '@/api/sp2Payload';
import { bufferNativeBars, clipEndBar, isTempoMatched, sessionBarsToNative, snapClipStart, timelineBars } from '@/audio/clipTiming';
import { resetClipLength, sliceRightOffset, trimClip } from '@/state/clipTrim';
import { fullLibraryLoop } from '@/audio/libraryLoopLength';
import { getBuffer, getCached } from '@/audio/BufferCache';
import {
  exportGuestBeat, guestSaveBody, loadGuestArrangement, saveGuestArrangement, startGuestSession,
} from '@/api/guest';
import { startPlayback, pausePlayback, stopPlayback, seekToBar, getIsPlaying, getIsPaused, updateSoloMute, cleanup as cleanupScheduler } from '@/audio/PlaybackScheduler';
import { getLoopAudioUrl, resolveAudioUrl, cacheAudioUrl } from '@/api/loops';
import { createProject, saveProject, loadProject, listProjects } from '@/api/projects';
import { renderToMp3 } from '@/audio/ExportRenderer';
import { ensureKnockBuffers, soundSettings } from '@/audio/knockRender';
import { arrangementSignature, createPlaybackRescheduler } from '@/state/playbackReschedule';
import { createGuestSessionGuard } from '@/state/guestSessionGuard';
import { coachStep } from '@/coach/coachRules';
import { PizzulGuide } from '@/components/Guide/PizzulGuide';
import { formatKioskRemaining, kioskTimerState, parseKioskParams } from '@/utils/kiosk';
import { regenerateKnockClip, resolveKnockContext } from '@/state/knockControls';
import { tempoLockState } from '@/state/tempoLock';
import { deleteSelectedEditableClips, selectAllClipIds } from '@/state/selection';
import { loopRegionFromSelection } from '@/state/loopSchedule';
import { findPasteBar } from '@/state/pastePlacement';
import { isRenderedClip } from '@/audio/clipKinds';
import { ensurePatternBuffers } from '@/audio/patternRender';
import { applyClipSound } from '@/state/clipSound';
import { useDawPageLock } from '@/theme/pageLock';
import { ensureStretchedBuffers } from '@/audio/stretchedBuffers';
import { applyLinkedPattern, createPatternClip, makePatternUnique, normalizePatternTracks } from '@/audio/patternModel';
import { findSound, getKits, getOneShotCategories, getOneShots, getSoundBuffer, loadProducerSounds } from '@/audio/producerSounds';
import { SYNTH_VOICES, applyInstrumentChoice, choiceFitsTrack, choiceType, currentChoiceForTrack, cycleChoice } from '@/audio/instrumentSwitch';
import { samplerDefaultsForSound } from '@/audio/sampler';
import { PatternEditor } from '@/components/PatternEditor/PatternEditor';
import { projectSessionFields, restoredKeySignature } from '@/state/projectSession';
import { useLiveInput } from '@/state/useLiveInput';
import { KeyboardDock } from '@/components/KeyboardDock';
import { commitDuplicateTrack } from '@/state/duplicateTrack';
import { buildBassTrack, buildMelodyTrack, commitMelodyIntoClip, commitStarterTrack } from '@/state/starterWrites';
import { defaultTrackEffects } from '@/state/trackModel';
import { commitTrackMixState, mergeTrackMix } from '@/state/trackMix';
import { getAudioContext, getMasterGain, setMasterVolume, updateMixGraph } from '@/audio/AudioEngine';
import { MixerPanel } from '@/components/Mixer/MixerPanel';
import { EditRow } from '@/components/Timeline/EditRow';
import { emptyLyrics, normalizeLyrics, safeLyrics } from '@/lyrics/schema';
import { ensureMarkerIds, sectionBars } from '@/lyrics/markers';
import { stableId } from '@/lyrics/ids';
import {
  clearCurrentGuestLyrics, guestSessionIdFromToken, readGuestLyrics, writeGuestLyrics,
} from '@/lyrics/storage';
import {
  autoSavePendingLyrics, clearPendingLyrics, pendingLyricsAction, readPendingLyrics,
} from '@/lyrics/pendingLyrics';
import { getClassPack } from '@/api/classPacks';
import { ClassSessionControls } from '@/components/Class/ClassSessionControls';
import { ClassCoach } from '@/components/Class/ClassCoach';
import { JoinPage, useQrHashChange } from '@/components/Class/JoinPage';
import { SeatSessionPage } from '@/components/Class/SeatSessionPage';
import { HomeSeatPage } from '@/components/Class/HomeSeatPage';
import { TakeHomeModal } from '@/components/Class/TakeHomeModal';
import {
  buildClassSession, buildMyBeat, countTrackSlots, editableTracks, isEditableClip, sendTrackToBank, toggleClassTrackMute,
} from '@/state/classSession';
import { isAudioEligible, rescheduleWhenAudioReady } from '@/audio/classAudio';
import { coachCopy, dismissCoachEvent, useClassCoachController } from '@/state/classCoach';
import { classFeatures } from '@/state/classFeatures';
import { addClassMapMarkers, classMapCanRefresh, classSongMap, classStepComplete, classStepsForFlow, recordClassProgressDone, selectClassSongMapId } from '@/state/classSteps';
import { deriveFurthestClassStep, seatSnapshot } from '@/state/seatSession';
import { trimClipAtEnd, clampClipStartAtEnd, copyClipAtEnd, flow2ClassEnd, growSongToFit, zoomToFitSong } from '@/state/classClipBounds';
import { createClassPlaybackTracker, updateClassPlaybackTracker } from '@/state/classPlaybackTracker';
import { classFlyMeter, classShowcaseText, classShowMeTarget, firstToolCheer } from '@/state/classGuideUi';

function downloadLyricsText(lyricsData) {
  const text = lyricsData.sections.map((section) => (
    `[${section.label}]\n${section.lines.map((line) => line.text).join('\n')}`
  )).join('\n\n');
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'guest-lyrics.txt';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function BeatArranger({ isGuest = false, eventName = null, kiosk = false, timerMinutes = null, classPack = null, canToggleClassFocus = false, seatMode = null, expoMode = null }) {
  useDawPageLock();
  const isExpo = Boolean(expoMode);
  const initialExpoSnapshot = useRef(expoMode?.initialSnapshot);
  const initialClassSession = useMemo(() => {
    if (seatMode?.free) {
      const saved = seatMode.beat?.arrangement || {};
      return {
        tracks: saved.tracks || [], sectionMarkers: saved.sectionMarkers || [],
        bpm: Number(saved.bpm) || 90, totalBars: Number(saved.totalBars) || 32,
      };
    }
    if (!classPack) return null;
    const built = isExpo ? expoSession(classPack) : buildClassSession(classPack);
    if (isExpo && initialExpoSnapshot.current) return { ...built, ...initialExpoSnapshot.current };
    if (!seatMode?.beat?.arrangement) return built;
    const saved = seatMode.beat.arrangement;
    return {
      tracks: saved.tracks || [],
      sectionMarkers: saved.sectionMarkers || [],
      classSession: saved.classSession || built.classSession,
      bpm: Number(saved.bpm) || built.bpm,
      totalBars: Number(saved.totalBars) || built.totalBars,
    };
  }, [classPack, seatMode?.beat, seatMode?.free, isExpo]);
  const isClass = Boolean(classPack || seatMode);
  const [expoHandoff, setExpoHandoff] = useState(expoMode?.initialHandoff || null);
  const [expoSaving, setExpoSaving] = useState(false);
  const expoSavingRef = useRef(false);
  const [expoError, setExpoError] = useState('');
  const [expoFilming, setExpoFilming] = useState(false);
  // --- Session-level state (NOT undoable per Section 6) ---
  const [projectName, setProjectName] = useState(seatMode ? (seatMode.title || `Beat ${seatMode.slot}`) : initialClassSession?.classSession.packName || (isGuest ? 'Workshop Beat' : 'My First Beat'));
  const [bpm, setBpm] = useState(initialClassSession?.bpm || 90);
  const [totalBars, setTotalBars] = useState(initialClassSession?.totalBars || 32);
  const [activeTool, setActiveTool] = useState('Select');
  const [copySpacing, setCopySpacing] = useState('auto');
  const [snapMode,setSnapMode] = useState('1');
  const [selectionRange,setSelectionRange] = useState(null);
  const [variationOpen,setVariationOpen] = useState(false);
  const [variationOmitted,setVariationOmitted] = useState(new Set());
  // playheadBar is 0-based internally. Bar 0 = first bar. Displayed as +1.
  const [playheadBar, setPlayheadBar] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [buffersReady, setBuffersReady] = useState(false);
  const [knockInFlight, setKnockInFlight] = useState(false);
  const [arrangementNotice, setArrangementNotice] = useState(null);
  const [kioskNow, setKioskNow] = useState(() => Date.now());
  const kioskTimerStartedAtRef = useRef(null);
  const [classMode, setClassMode] = useState({ focus: seatMode ? Boolean(seatMode.focus) : true, canToggleFocus: Boolean(canToggleClassFocus) });
  const [furthestStep, setFurthestStep] = useState(() => seatMode ? deriveFurthestClassStep(initialClassSession) : 0);
  const [skippedSteps, setSkippedSteps] = useState(() => new Set());
  const [classCoachNotice, setClassCoachNotice] = useState(null);
  const [classHighlight, setClassHighlight] = useState(null);
  const [unavailableTrackIds, setUnavailableTrackIds] = useState(() => new Set());
  const usedClassToolsRef = useRef(new Set());

  // --- Project persistence ---
  const [projectId, setProjectId] = useState(() => isClass ? null : localStorage.getItem('beat_arranger_project_id'));
  const [saveStatus, setSaveStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [exportStatus, setExportStatus] = useState(null); // null | 'rendering' | 'success' | 'error'
  const [projectDirty, setProjectDirty] = useState(false);
  const [lyricsDirty, setLyricsDirty] = useState(false);
  const projectTrackingRef = useRef(false);
  const suppressNextProjectDirtyRef = useRef(false);

  // --- Layout state ---
  const [libraryCollapsed, setLibraryCollapsed] = useState(false);
  const [libraryCoachCommand, setLibraryCoachCommand] = useState(null);
  const [tempoLockReleased, setTempoLockReleased] = useState(false);
  const [zoom, setZoom] = useState(1);
  const timelineWidthRef = useRef(0);
  const manualClassZoomRef = useRef(false);
  const [producerMode, setProducerModeState] = useState(() => {
    if (kiosk || isClass) return false;
    try { return localStorage.getItem('ba_producer_mode') === '1'; } catch (e) { return false; }
  });
  const [editingPatternId, setEditingPatternId] = useState(null);
  const [mixerOpen, setMixerOpen] = useState(false);
  const [masterVolume, setMasterVolumeState] = useState(100);
  const patternLoopRestoreRef = useRef(null);
  const patternLoadNoticeShownRef = useRef(false);
  const patternLoadMessage = 'A pattern could not load its sounds';

  // --- Auth state ---
  const { user, isAuthenticated, login, logout, getToken } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalContext, setAuthModalContext] = useState(null); // 'save' | 'projects' | null
  const [authModalMessage, setAuthModalMessage] = useState(null);
  const pendingSaveRef = useRef(false);

  // --- Modal state ---
  const [autoArrangerOpen, setAutoArrangerOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [workshopExportOpen, setWorkshopExportOpen] = useState(false);
  const [guestExportOpen, setGuestExportOpen] = useState(false);
  const [takeHomeOpen, setTakeHomeOpen] = useState(() => Boolean(seatMode?.openTakeHome));
  const [isExporting, setIsExporting] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [sectionMarkerOpen, setSectionMarkerOpen] = useState(false);
  const [newBeatPrompt, setNewBeatPrompt] = useState(null); // visitor | signed-in | guest
  const [newBeatSaveError, setNewBeatSaveError] = useState(false);
  const [importPrompt, setImportPrompt] = useState(null);
  const [importError, setImportError] = useState(null);

  // --- Clipboard & Selection ---
  const [clipboard, setClipboard] = useState(null);
  const [selectedClipIds, setSelectedClipIds] = useState(new Set());
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopRegion, setLoopRegion] = useState({ startBar: 0, endBar: 4 });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const lastPasteEndBarRef = useRef(null); // For sequential paste advancement

  // Multi-select handler: normal click = select one, shift+click = toggle
  // Resets lastPasteEndBar on selection change
  const handleSelectClip = useCallback((clipId, shiftKey) => {
    lastPasteEndBarRef.current = null; // Reset on selection change
    setClassHighlight((current) => typeof current === 'object' ? null : current);
    if (clipId === null) {
      setSelectionRange(null);
      setSelectedClipIds(new Set());
      return;
    }
    if (shiftKey) {
      setSelectedClipIds((prev) => {
        const next = new Set(prev);
        if (next.has(clipId)) next.delete(clipId);
        else next.add(clipId);
        return next;
      });
    } else {
      setSelectionRange(null);
      setSelectedClipIds(new Set([clipId]));
    }
  }, []);

  // --- Audio Preview ---
  const [previewingLoopId, setPreviewingLoopId] = useState(null);
  const previewSourceRef = useRef(null);

  // --- Guest Session (Section 10) ---
  const [guestToken, setGuestToken] = useState(() => (
    localStorage.getItem('beat_arranger_guest_event') === eventName
      ? localStorage.getItem('beat_arranger_guest_token') : null
  ));
  const guestHydratedRef = useRef(false);
  const lastAcknowledgedRef = useRef(null);
  const [guestDirty, setGuestDirty] = useState(false);
  const [leavePrompt, setLeavePrompt] = useState(false);
  const [leaveSaveTimedOut, setLeaveSaveTimedOut] = useState(false);
  const allowGuardNavigationRef = useRef(false);
  const guestDirtyRef = useRef(false);
  const pendingHistoryTargetRef = useRef(null);
  const guestSessionGuardRef = useRef(createGuestSessionGuard());
  const guestInitInFlightRef = useRef(false);
  const guestResetInProgressRef = useRef(false);

  // Lyrics stay outside arrangement undo/redo history.
  const initialGuestSessionId = guestSessionIdFromToken(guestToken);
  const [lyrics, setLyrics] = useState(() => (
    seatMode ? safeLyrics(seatMode.beat.lyrics)
      : isGuest ? safeLyrics(readGuestLyrics(localStorage, initialGuestSessionId)) : emptyLyrics()
  ));
  const [lyricsOpen, setLyricsOpen] = useState(() => localStorage.getItem('ba_lyrics_open') === '1');
  const [lyricsImportOverflow, setLyricsImportOverflow] = useState(0);
  const [lyricsUndoResetKey, setLyricsUndoResetKey] = useState(0);
  const [pendingLyricsBanner, setPendingLyricsBanner] = useState(null);
  const pendingLyricsRetryRef = useRef(false);
  const guestLyricsSessionRef = useRef(initialGuestSessionId);
  const guestLyricsSkipWriteRef = useRef(null);

  // --- Reflect Session ID (from SP2.0 payload) ---
  const [reflectSessionId, setReflectSessionId] = useState(null);
  const [keySignature, setKeySignature] = useState(null);

  const hasImport = useRef(hasSP2Import());
  const pendingImportRef = useRef(null);

  // --- Undo/Redo (arrangement state: tracks + sectionMarkers) ---
  const { state: arrangement, pushState, updateState, undo, redo, reset: resetHistory, canUndo, canRedo } = useUndoRedo(
    initialClassSession ? {
      tracks: initialClassSession.tracks,
      sectionMarkers: initialClassSession.sectionMarkers,
      classSession: initialClassSession.classSession,
    } : { tracks: [], sectionMarkers: [] }
  );

  const librarySessionRef = useRef(0);
  const librarySnapshotRef = useRef(arrangement);
  librarySnapshotRef.current = arrangement;
  useEffect(() => () => { librarySessionRef.current += 1; }, []);
  const tracks = arrangement.tracks;
  const sectionMarkers = arrangement.sectionMarkers;
  const lastSectionLength=useRef(totalBars);
  useEffect(()=>{
    const length=arrangement.sectionSongLength;
    if(Number.isFinite(length)){
      const previous=lastSectionLength.current;
      setTotalBars(current=>current===previous||current===length?length:current);
      lastSectionLength.current=length;
    }
  },[arrangement.sectionSongLength]);
  const classSession = arrangement.classSession;
  const flow2Map = useMemo(() => classSession?.flow === 2 ? classSongMap(bpm, 2, classSession.mapId) : null, [bpm, classSession?.flow, classSession?.mapId]);
  const flyMeter = useMemo(() => classSession?.flow === 2 ? classFlyMeter(tracks, bpm, classSession) : null, [tracks, bpm, classSession]);
  const listenComplete = classSession?.flow === 2 && classStepComplete('listen', { tracks, sectionMarkers, bpm, classSession });
  const showcaseText = flow2Map ? classShowcaseText(flow2Map.length, bpm, listenComplete) : null;
  const featureFlags = useMemo(() => {
    if (expoMode) return {
      producer: false, add: false, library: false, autoArrange: false, newBeat: false,
      openProjects: false, save: false, keyboard: false, record: false, mixer: false,
      lyrics: false, clipSound: classSession?.phase === 'arrange' && !expoHandoff, export: classSession?.phase === 'arrange' && !expoHandoff,
    };
    const features = classFeatures({
      isClass: Boolean(classSession), focus: classMode.focus, furthestStep,
      takehomeOpen: Boolean(seatMode?.takehomeOpen),
      flow: classSession?.flow,
      free: Boolean(seatMode?.free),
      readOnly: Boolean(seatMode?.readOnly),
      recordingEnabled: Boolean(seatMode?.recordingEnabled),
    });
    if (seatMode && !seatMode.free) features.lyrics = !classMode.focus || seatMode.unlockedAll || seatMode.lyricsEnabled;
    return features;
  }, [classSession, classMode.focus, furthestStep, seatMode, expoMode, expoHandoff]);
  const tracksRef = useRef(tracks);
  tracksRef.current = tracks;
  const bpmRef = useRef(bpm);
  bpmRef.current = bpm;
  const classSessionRef = useRef(classSession);
  classSessionRef.current = classSession;
  const loopEnabledRef = useRef(loopEnabled);
  loopEnabledRef.current = loopEnabled;
  const playbackTrackerRef = useRef(createClassPlaybackTracker(initialClassSession?.classSession?.progress));
  const [mixDrafts, setMixDrafts] = useState({});
  const mixDraftsRef = useRef({});
  const [coachController, setCoachController] = useClassCoachController(Boolean(classSession), tracks);
  const seatEditorReadyRef = useRef(false);

  useEffect(() => {
    if (!seatMode || seatMode.free) return;
    setClassMode({ focus: Boolean(seatMode.focus), canToggleFocus: false });
    // Only the server-driven focus value matters here; seatMode is rebuilt on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seatMode?.focus, seatMode?.free]);

  useEffect(() => {
    if (!seatMode?.onChange) return;
    const snapshot = seatSnapshot(arrangement, normalizeLyrics(lyrics), bpm, totalBars);
    if (!seatEditorReadyRef.current) {
      seatEditorReadyRef.current = true;
      seatMode.onReady?.(snapshot);
      return;
    }
    seatMode.onChange(snapshot);
    // Keyed on the stable callbacks, not the seatMode object the parent rebuilds each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrangement, bpm, lyrics, seatMode?.onChange, seatMode?.onReady, totalBars]);

  useEffect(() => {
    expoMode?.onSnapshot?.({ tracks, sectionMarkers, classSession, bpm, totalBars });
    // The parent callback is stable; no Expo lyrics or account data enter this snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracks, sectionMarkers, classSession, bpm, totalBars, expoMode?.onSnapshot]);

  const showBlockedClassAction = useCallback(() => {
    if (!isClass) return;
    setClassCoachNotice({ kind: 'blocked', pose: 'confused', line: coachCopy.blockedActions[0] });
  }, [isClass]);

  const celebrateClassTool = useCallback((tool) => {
    if (classSessionRef.current?.flow !== 2) return;
    const line = firstToolCheer(tool, usedClassToolsRef.current);
    if (line) setClassCoachNotice({ kind: 'tool-cheer', pose: 'happy', line });
  }, []);

  const clearClassHighlight = useCallback(() => setClassHighlight((current) => typeof current === 'object' ? null : current), []);

  useEffect(() => {
    if (!['cheer', 'tool-cheer'].includes(classCoachNotice?.kind)) return undefined;
    const timer = setTimeout(() => setClassCoachNotice(null), 1800);
    return () => clearTimeout(timer);
  }, [classCoachNotice]);

  useEffect(() => {
    if (!classHighlight) return undefined;
    const timer = setTimeout(() => setClassHighlight(null), typeof classHighlight === 'object' ? 6000 : 2400);
    return () => clearTimeout(timer);
  }, [classHighlight]);

  useEffect(() => {
    if (isExpo || classSession?.phase !== 'arrange') return;
    const steps = classStepsForFlow(classSession?.flow);
    const next = classSession?.flow === 2
      ? deriveFurthestClassStep({ tracks, sectionMarkers, bpm, classSession })
      : (() => {
        const step = steps[furthestStep];
        if (!step || furthestStep >= steps.length - 1) return furthestStep;
        return skippedSteps.has(furthestStep) || classStepComplete(step.id, { tracks, sectionMarkers, bpm })
          ? Math.min(steps.length - 1, furthestStep + 1) : furthestStep;
      })();
    if (next <= furthestStep) return;
    if (classSession?.flow === 2) {
      updateState((current) => ({
        ...current,
        classSession: recordClassProgressDone(current.classSession, furthestStep, next),
      }));
    }
    setFurthestStep(next);
    setClassCoachNotice((current) => current?.kind === 'tool-cheer'
      ? current : { kind: 'cheer', pose: 'happy', line: coachCopy.cheers[furthestStep % coachCopy.cheers.length] });
  }, [bpm, classSession, furthestStep, sectionMarkers, skippedSteps, tracks, updateState, isExpo]);

  useEffect(() => {
    if (classSession?.flow !== 2 || classSession.phase !== 'arrange') return;
    setTotalBars(classSongMap(bpm, 2, classSession.mapId).length);
  }, [bpm, classSession?.flow, classSession?.mapId, classSession?.phase]);

  useEffect(() => {
    if (classSession?.flow !== 2 || classSession.phase !== 'arrange') return;
    manualClassZoomRef.current = false;
    if (timelineWidthRef.current > 0) setZoom(zoomToFitSong(classSongMap(bpmRef.current, 2, classSession.mapId).length, timelineWidthRef.current));
  }, [classSession?.flow, classSession?.mapId, classSession?.phase]);

  const handleTimelineResize = useCallback((width) => {
    timelineWidthRef.current = width;
    const session = classSessionRef.current;
    if (session?.flow === 2 && session.phase === 'arrange' && !manualClassZoomRef.current && width > 0) {
      setZoom(zoomToFitSong(classSongMap(bpmRef.current, 2, session.mapId).length, width));
    }
  }, []);

  const fitClassSong = useCallback(() => {
    if (!flow2Map || timelineWidthRef.current <= 0) return;
    manualClassZoomRef.current = true;
    setZoom(zoomToFitSong(flow2Map.length, timelineWidthRef.current));
  }, [flow2Map]);

  const changeZoom = useCallback((direction) => {
    manualClassZoomRef.current = true;
    const minimum = classSessionRef.current?.flow === 2 ? 0.25 : 0.5;
    setZoom((current) => Math.max(minimum, Math.min(3, Number((current + direction * 0.1).toFixed(2)))));
  }, []);

  // The song grows to fit its clips. Paste, duplicate and Auto Arrange can place clips past the
  // song end; those never played, and the timeline scrolled past a ruler that could not follow.
  useEffect(() => {
    const lastEnd = tracks.reduce((end, track) => (track.clips || []).reduce((e, clip) => Math.max(e, clipEndBar(clip, bpm)), end), 0);
    const mapLength = flow2ClassEnd(classSession, bpm);
    const next = growSongToFit(lastEnd, totalBars, { flow: classSession?.flow, mapLength, maxBars: BARS_RANGE.max });
    if (next !== totalBars) setTotalBars(next);
  }, [tracks, bpm, totalBars, classSession]);

  // Selection and loop helpers read `tracks`, so they must come after it is defined
  // (above it they threw "Cannot access 'tracks' before initialization" on every render).
  const selectAllClips = useCallback(() => {
    setSelectionRange(null);
    lastPasteEndBarRef.current = null;
    setSelectedClipIds(selectAllClipIds(tracks));
  }, [tracks]);

  const selectTrackClips = useCallback((trackId, shiftKey) => {
    if (!editableTracks(tracks).some((track) => track.id === trackId)) return;
    setSelectedTrackId(trackId);
    lastPasteEndBarRef.current = null;
    const ids = tracks.find((track) => track.id === trackId)?.clips?.map((clip) => clip.id) || [];
    setSelectedClipIds((previous) => shiftKey ? new Set([...previous, ...ids]) : new Set(ids));
  }, [tracks]);

  // The Loop button, L and a click on the loop bar only turn looping on or off. They never move
  // the loop: you set where it is on the ruler's loop strip (or with "Loop this" on a clip).
  const toggleLoop = useCallback(() => setLoopEnabled((on) => !on), []);

  const setLoopFromRuler = useCallback((region, { enable = true } = {}) => {
    setLoopRegion(region);
    if (enable) setLoopEnabled(true);
  }, []);

  // "Loop this" on a clip: loops the whole selection if that clip is part of it, else just the clip.
  const loopClip = useCallback((clipId) => {
    if (!isEditableClip(tracks, clipId)) return;
    const ids = selectedClipIds.has(clipId) ? selectedClipIds : new Set([clipId]);
    const region = loopRegionFromSelection(tracks, ids, bpm);
    if (!region) return;
    setLoopRegion(region);
    setLoopEnabled(true);
  }, [selectedClipIds, tracks, bpm]);

  const tempoLock = useMemo(() => {
    const state = tempoLockState({ tracks, sessionBpm: bpm, released: tempoLockReleased });
    return {
      ...state,
      hasLibraryLoops: tracks.some((track) => (track.clips || []).some((clip) => !isRenderedClip(clip))),
    };
  }, [tracks, bpm, tempoLockReleased]);
  useEffect(() => {
    if (tracks.length === 0) setTempoLockReleased(false);
  }, [tracks.length]);
  useEffect(() => {
    if (suppressNextProjectDirtyRef.current) {
      suppressNextProjectDirtyRef.current = false;
      return;
    }
    if (projectTrackingRef.current) setProjectDirty(true);
    else projectTrackingRef.current = true;
  }, [tracks, sectionMarkers]);
  const tempoMismatchCount = useMemo(() => tracks.reduce(
    (count, track) => count + track.clips.filter((clip) => !isTempoMatched(clip, bpm)).length,
    0,
  ), [tracks, bpm]);
  const currentCoachStep = useMemo(() => isClass ? null : coachStep({ tracks, sectionMarkers, tempoMismatchCount, isGuest, sessionBpm: bpm, selectedCount: selectedClipIds.size }), [isClass, tracks, sectionMarkers, tempoMismatchCount, isGuest, bpm, selectedClipIds]);
  const kioskTimer = kioskTimerState(kioskTimerStartedAtRef.current, kioskNow, timerMinutes);

  useEffect(() => {
    if (!kiosk || !timerMinutes || tracks.length === 0 || kioskTimerStartedAtRef.current) return;
    kioskTimerStartedAtRef.current = Date.now();
    setKioskNow(Date.now());
  }, [kiosk, timerMinutes, tracks.length]);
  useEffect(() => { if (kiosk) setProducerModeState(false); }, [kiosk]);
  useEffect(() => {
    if (arrangementNotice !== patternLoadMessage) return undefined;
    const timer = setTimeout(() => setArrangementNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [arrangementNotice]);
  const setProducerMode = useCallback((enabled) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    const next = !kiosk && Boolean(enabled);
    setProducerModeState(next);
    try { localStorage.setItem('ba_producer_mode', next ? '1' : '0'); } catch (e) {}
  }, [featureFlags.producer, kiosk, showBlockedClassAction]);
  useEffect(() => {
    if (!kiosk || !timerMinutes || !kioskTimerStartedAtRef.current) return undefined;
    const interval = setInterval(() => setKioskNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [kiosk, timerMinutes, tracks.length]);


  // Booth imports are staged before URL cleanup, resolved off-screen, then committed together.
  const importDirtyRef = useRef(false);
  importDirtyRef.current = projectDirty || guestDirty || lyricsDirty;
  const applyImport = useCallback(async (payload) => {
    pendingImportRef.current = payload;
    setImportError(null);
    try {
      const result = payloadToTracks(payload);
      const audioJobs = result.tracks.flatMap((track) => track.clips
        .filter(() => isAudioEligible(track))
        .filter((clip) => !isRenderedClip(clip))
        .map((clip) => getBuffer(clip.loop_id, resolveAudioUrl(clip.loop_id, clip))));
      await Promise.race([
        Promise.all([
          ...audioJobs,
          ensureKnockBuffers(result.tracks, result.bpm),
          ensurePatternBuffers(result.tracks, result.bpm),
        ]),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Import timed out')), 12000)),
      ]);

      setTempoLockReleased(false);
      setBpm(result.bpm);
      setKeySignature(result.key || null);
      setReflectSessionId(result.reflectSessionId || null);
      setTotalBars(result.totalBars || 32);
      setProjectName(result.lyrics?.title || 'Untitled Beat');
      setProjectId(null);
      localStorage.removeItem('beat_arranger_project_id');
      suppressNextProjectDirtyRef.current = true;
      librarySessionRef.current += 1;
      resetHistory({
        tracks: result.tracks,
        sectionMarkers: ensureMarkerIds(result.markers || []),
      });
      setLyrics(result.lyrics || emptyLyrics());
      setLyricsImportOverflow(result.overflowCount || 0);
      setLyricsUndoResetKey((value) => value + 1);
      setSelectedClipIds(new Set());
      setPlayheadBar(0);
      setProjectDirty(true);
      setLyricsDirty(Boolean(result.lyrics));
      clearPendingImport();
      pendingImportRef.current = null;
    } catch (error) {
      setImportError("Couldn't open that song");
    }
  }, [resetHistory]);

  const receiveImport = useCallback((payload) => {
    if (!payload) return;
    pendingImportRef.current = payload;
    if (importDirtyRef.current) setImportPrompt(payload);
    else applyImport(payload);
  }, [applyImport]);

  useEffect(() => {
    const parseImport = () => {
      const payload = parseSP2Payload();
      if (payload) receiveImport(payload);
      else hasImport.current = false;
    };
    parseImport();
    window.addEventListener('hashchange', parseImport);
    return () => window.removeEventListener('hashchange', parseImport);
  }, [receiveImport]);

  // =====================================================================
  // GUEST SESSION INIT — Section 10.1
  // Auto-create guest session on /workshop mount if no existing token
  // =====================================================================
  useEffect(() => {
    if (!isGuest) return;
    if (guestResetInProgressRef.current) return;
    if (guestToken) return; // Already have a session
    // One creation at a time: React dev mode runs effects twice, which made two
    // sessions and left an empty orphan in the facilitator's counts.
    if (guestInitInFlightRef.current) return;
    guestInitInFlightRef.current = true;

    const init = async () => {
      try {
        const result = await startGuestSession(eventName);
        setGuestToken(result.session_token);
        localStorage.setItem('beat_arranger_guest_token', result.session_token);
        localStorage.setItem('beat_arranger_guest_event', eventName || '');
      } catch (e) {
        console.warn('Guest session init failed:', e.message);
      } finally {
        guestInitInFlightRef.current = false;
      }
    };
    init();
  }, [isGuest, guestToken, eventName]);

  useEffect(() => {
    if (!isGuest || !guestToken || hasImport.current) return;
    const sessionId = guestSessionIdFromToken(guestToken);
    if (!sessionId || guestLyricsSessionRef.current === sessionId) return;
    guestLyricsSessionRef.current = sessionId;
    guestLyricsSkipWriteRef.current = sessionId;
    setLyrics(safeLyrics(readGuestLyrics(localStorage, sessionId)));
    setLyricsDirty(false);
  }, [isGuest, guestToken]);

  useEffect(() => {
    if (!isGuest) return;
    const sessionId = guestSessionIdFromToken(guestToken);
    if (!sessionId || guestLyricsSessionRef.current !== sessionId) return;
    if (guestLyricsSkipWriteRef.current === sessionId) {
      guestLyricsSkipWriteRef.current = null;
      return;
    }
    writeGuestLyrics(localStorage, sessionId, lyrics);
  }, [isGuest, guestToken, lyrics]);

  useEffect(() => {
    localStorage.setItem('ba_lyrics_open', lyricsOpen ? '1' : '0');
  }, [lyricsOpen]);

  useEffect(() => {
    if (readPendingLyrics(localStorage)) setPendingLyricsBanner('local-only');
  }, []);

  // Reloading restores exactly the last 2xx-acknowledged autosave.  We wait for
  // this before scheduling any write so a blank client cannot overwrite it.
  useEffect(() => {
    if (!isGuest || !guestToken || guestHydratedRef.current || hasImport.current) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await loadGuestArrangement(guestToken);
        if (cancelled) return;
        const restored = result.arrangement_data || { tracks: [], sectionMarkers: [] };
        // Tempo, key, length and name ride along in the same blob; without them a reload put a
        // 70 BPM beat back at the default 90 and every loop showed as off-tempo.
        const session = restored.session || {};
        if (Number(session.bpm) > 0) setBpm(Number(session.bpm));
        setKeySignature(session.keySignature || null);
        if (Number(session.totalBars) > 0) setTotalBars(Number(session.totalBars));
        if (session.projectName) setProjectName(session.projectName);
        lastAcknowledgedRef.current = JSON.stringify(restored);
        const restoredTracks = normalizePatternTracks(restored.tracks || []);
        await ensurePatternBuffers(restoredTracks, Number(session.bpm) > 0 ? Number(session.bpm) : 90).catch((error) => console.warn('Guest pattern render failed:', error.message));
        suppressNextProjectDirtyRef.current = true;
        pushState({
          tracks: restoredTracks,
          sectionMarkers: ensureMarkerIds(restored.sectionMarkers || []),
        });
        setLyricsDirty(false);
      } catch (e) {
        // A new or expired session is still usable; only confirmed saves count.
        lastAcknowledgedRef.current = JSON.stringify({ tracks: [], sectionMarkers: [] });
        console.warn('Guest session restore failed:', e.message);
      } finally {
        if (!cancelled) guestHydratedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [isGuest, guestToken, pushState]);

  const guestSnapshot = useCallback(() => ({
    tracks, sectionMarkers,
    session: { bpm, keySignature, totalBars, projectName },
  }), [tracks, sectionMarkers, bpm, keySignature, totalBars, projectName]);

  const handleLyricsChange = useCallback((nextLyrics) => {
    if (!featureFlags.lyrics) { showBlockedClassAction(); return; }
    setLyrics(nextLyrics);
    setLyricsDirty(true);
    if (isGuest) setGuestDirty(true);
    else setProjectDirty(true);
  }, [featureFlags.lyrics, isGuest, showBlockedClassAction]);

  useEffect(() => {
    if (isGuest || !isAuthenticated || !user?.id) return;
    const pending = readPendingLyrics(localStorage);
    const action = pendingLyricsAction(pending, user.id);
    if (action === 'retry' && !pendingLyricsRetryRef.current) {
      pendingLyricsRetryRef.current = true;
      setPendingLyricsBanner('saving');
      autoSavePendingLyrics(
        { ...pending, lyrics: safeLyrics(pending.lyrics) },
        user.id,
        saveProject,
      )
        .then(() => {
          if (projectId === pending.projectId) {
            setLyrics(safeLyrics(pending.lyrics));
            setLyricsDirty(false);
          }
          clearPendingLyrics(localStorage);
          clearCurrentGuestLyrics(localStorage, pending.guestSessionId);
          setPendingLyricsBanner(null);
        })
        .catch(() => setPendingLyricsBanner('saving'))
        .finally(() => { pendingLyricsRetryRef.current = false; });
    } else if (action === 'local-only') {
      setPendingLyricsBanner('local-only');
    }
  }, [isGuest, isAuthenticated, projectId, user?.id]);

  const saveGuestNow = useCallback(async () => {
    if (!guestToken || !guestHydratedRef.current) return;
    const generation = guestSessionGuardRef.current.current();
    const token = guestToken;
    const snapshot = guestSnapshot();
    await saveGuestArrangement(token, guestSaveBody(snapshot));
    // A Next student reset may have happened while the request was in flight.
    // Never acknowledge (or clear dirty state for) that old session afterwards.
    if (!guestSessionGuardRef.current.isCurrent(generation)) return;
    lastAcknowledgedRef.current = JSON.stringify(snapshot);
    setGuestDirty(false);
  }, [guestToken, guestSnapshot]);

  useEffect(() => {
    if (!isGuest || !guestHydratedRef.current) return;
    setGuestDirty(JSON.stringify(guestSnapshot()) !== lastAcknowledgedRef.current);
  }, [isGuest, guestSnapshot]);

  useEffect(() => { guestDirtyRef.current = guestDirty; }, [guestDirty]);

  // Guest auto-save (debounced, saves arrangement to D1 on changes)
  const guestSaveTimer = useRef(null);
  useEffect(() => {
    if (!isGuest || !guestToken || !guestHydratedRef.current) return;
    if (guestSaveTimer.current) clearTimeout(guestSaveTimer.current);
    guestSaveTimer.current = setTimeout(async () => {
      try {
        await saveGuestNow();
      } catch (e) {
        console.warn('Guest autosave failed:', e.message);
      }
    }, 3000);
    return () => { if (guestSaveTimer.current) clearTimeout(guestSaveTimer.current); };
  }, [isGuest, guestToken, tracks, sectionMarkers, saveGuestNow]);

  // Guest export handler
  const handleGuestExport = useCallback(async (email) => {
    if (!featureFlags.export) { showBlockedClassAction(); return null; }
    if (!guestToken) throw new Error('Workshop session is not ready yet');
    setIsExporting(true);
    try {
      // Save final arrangement before export
      await saveGuestNow();
      const mp3Blob = await renderToMp3(tracks, bpm, totalBars, () => {});
      if (!mp3Blob || mp3Blob.size === 0) throw new Error('Could not render a playable MP3');
      // Fresh UUID per deliberate Export click: retries preserve their key only
      // when the browser retries the same request, never across edits/clicks.
      const result = await exportGuestBeat(guestToken, email, mp3Blob, crypto.randomUUID(), projectName);
      setGuestDirty(false);
      return result;
    } finally {
      setIsExporting(false);
    }
  }, [featureFlags.export, guestToken, tracks, bpm, totalBars, projectName, saveGuestNow, showBlockedClassAction]);

  // One sentinel entry is kept behind the active workshop page.  popstate has
  // already moved history when it fires, so restoring a sentinel with pushState
  // discards that attempted traversal while preserving a single-step escape.
  useEffect(() => {
    if (!isGuest) return;
    const sentinel = { beatArrangerGuard: crypto.randomUUID() };
    window.history.pushState(sentinel, '', window.location.href);
    const onPopState = () => {
      if (allowGuardNavigationRef.current) {
        allowGuardNavigationRef.current = false;
        return;
      }
      if (!guestDirtyRef.current) return;
      // This URL is the browser's already-selected back *or forward* target.
      // Pushing the replacement sentinel preserves it as the route to execute
      // after confirmation instead of guessing a history delta.
      pendingHistoryTargetRef.current = window.location.href;
      window.history.pushState(sentinel, '', window.location.href);
      setLeaveSaveTimedOut(false);
      setLeavePrompt(true);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [isGuest]);

  useEffect(() => {
    if (!isGuest || !guestDirty) return;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ''; // browsers (including mobile Safari) choose their own copy
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isGuest, guestDirty]);

  const leaveWorkshop = useCallback(async (force = false) => {
    if (!force) {
      let timedOut = false;
      try {
        await Promise.race([
          saveGuestNow(),
          new Promise((resolve) => setTimeout(() => { timedOut = true; resolve(); }, 3000)),
        ]);
      } catch (e) { timedOut = true; }
      if (timedOut) { setLeaveSaveTimedOut(true); return; }
    }
    setLeavePrompt(false);
    // Do not use an unqualified history.back(): it would be correct for a
    // back gesture but wrong for a guarded forward gesture. Navigate to the
    // captured target URL, which is the actual route selected by popstate.
    guestDirtyRef.current = false;
    setGuestDirty(false);
    const target = pendingHistoryTargetRef.current;
    pendingHistoryTargetRef.current = null;
    if (target) window.location.assign(target);
    else {
      allowGuardNavigationRef.current = true;
      window.history.back();
    }
  }, [saveGuestNow]);

  // =====================================================================
  // AUDIO PLAYBACK — Wire PlaybackScheduler to UI
  // =====================================================================

  // Prefetch all audio buffers when tracks change
  // Note: On initial page load (SP2 import), AudioContext may be suspended (no user gesture yet).
  // If prefetch fails due to suspended context, handlePlayPause will retry with user gesture.
  useEffect(() => {
    const doPrefetch = async () => {
      try {
        const { getBuffer } = await import('@/audio/BufferCache');
        const { getAudioContext } = await import('@/audio/AudioEngine');
        const { fetchLoop } = await import('@/api/loops');
        const ctx = getAudioContext();
        // If AudioContext is suspended (no user gesture yet), skip prefetch — Play will handle it
        if (ctx.state === 'suspended') {
          console.log('[Prefetch] AudioContext suspended — will prefetch on first Play');
          return;
        }

        const uniqueLoops = new Map();
        for (const track of tracks) {
          if (!isAudioEligible(track)) continue;
          for (const clip of track.clips) {
            if (isRenderedClip(clip)) continue;
            if (!uniqueLoops.has(clip.loop_id)) {
              // Priority: clip.audio_url (via proxy) > cached SP2 URL > needs metadata fetch
              if (clip.audio_url) cacheAudioUrl(clip.loop_id, clip.audio_url);
              let url = resolveAudioUrl(clip.loop_id, clip);
              uniqueLoops.set(clip.loop_id, url);
            }
          }
        }

        // For loops without cached audio_url (imported from SP2 without audio_url),
        // fetch metadata from the API to get the audio_url
        const toFetchMeta = [];
        for (const [loopId, url] of uniqueLoops) {
          // If URL is the proxy fallback (/api/v1/loops/:id/audio), try to get real URL
          if (url.includes('/api/v1/loops/') && url.endsWith('/audio')) {
            toFetchMeta.push(loopId);
          }
        }
        if (toFetchMeta.length > 0) {
          const metaPromises = toFetchMeta.map(async (loopId) => {
            try {
              const meta = await fetchLoop(loopId);
              if (meta && meta.audio_url) {
                cacheAudioUrl(loopId, meta.audio_url);
                uniqueLoops.set(loopId, meta.audio_url);
              }
            } catch (e) {
              console.warn(`Metadata fetch for ${loopId}:`, e.message);
            }
          });
          await Promise.all(metaPromises);
        }

        // Now fetch and decode all audio buffers
        const promises = [];
        for (const [loopId, url] of uniqueLoops) {
          promises.push(getBuffer(loopId, url).catch((err) => {
            console.warn(`Prefetch: failed for ${loopId}:`, err.message);
            setUnavailableTrackIds((current) => new Set([...current, ...tracks.filter((track) => track.classSlot && track.clips?.some((clip) => clip.loop_id === loopId)).map((track) => track.id)]));
          }));
        }
        await Promise.all(promises);
        setBuffersReady(true);
      } catch (e) {
        console.warn('Prefetch failed:', e.message);
      }
    };
    if (tracks.length > 0) {
      doPrefetch();
    }
  }, [tracks]);

  // Cleanup scheduler on unmount
  useEffect(() => {
    return () => cleanupScheduler();
  }, []);

  const trackClassPlayback = useCallback((event) => {
    const session = classSessionRef.current;
    if (session?.flow !== 2) return;
    const map = classSongMap(bpmRef.current, 2, session.mapId);
    const hookStart = map.sections.find((section) => section.name === 'Hook')?.startBar || 0;
    const tracked = updateClassPlaybackTracker(playbackTrackerRef.current, event, {
      hookStart, mapLength: map.length, loopEnabled: loopEnabledRef.current,
    });
    playbackTrackerRef.current = tracked.state;
    if (!Object.keys(tracked.changed).length) return;
    updateState((current) => {
      const currentSession = current.classSession;
      if (currentSession?.flow !== 2) return current;
      return {
        ...current,
        classSession: {
          ...currentSession,
          progress: { skipped: [], ...(currentSession.progress || {}), ...tracked.changed },
        },
      };
    });
  }, [updateState]);

  // Playhead callback from RAF — updates React state and persisted class listening progress.
  const handlePlayheadUpdate = useCallback((barPosition) => {
    setPlayheadBar(barPosition);
    trackClassPlayback({ type: 'tick', bar: barPosition });
  }, [trackClassPlayback]);

  // Playback end callback
  const handlePlaybackEnd = useCallback(() => {
    trackClassPlayback({ type: 'stop', bar: 0 });
    setIsPlaying(false);
    setIsPaused(false);
    setPlayheadBar(0);
  }, [trackClassPlayback]);

  const liveInput = useLiveInput({
    tracks, sectionMarkers, classSession, pushState, bpm, totalBars, playheadBar, loopEnabled, loopRegion, keySignature, maxTracks: MAX_TRACKS,
    onPlayingChange: (playing, paused) => { setIsPlaying(playing); setIsPaused(paused); },
    onPlayheadUpdate: handlePlayheadUpdate, onPlaybackEnd: handlePlaybackEnd, onNotice: setArrangementNotice,
  });
  const displayTracks = liveInput.liveTracks.map((track) => mixDrafts[track.id] ? mergeTrackMix(track, mixDrafts[track.id]) : track);

  const openExport = useCallback(() => {
    if (!featureFlags.export) { showBlockedClassAction(); return; }
    if (isGuest) setGuestExportOpen(true);
    else if (!isAuthenticated) setWorkshopExportOpen(true);
    else { setExportOpen(true); setExportStatus(null); }
  }, [featureFlags.export, isAuthenticated, isGuest, showBlockedClassAction]);

  const handleRecord = useCallback(() => {
    if (!featureFlags.record) { showBlockedClassAction(); return; }
    liveInput.toggleRecord();
  }, [featureFlags.record, liveInput, showBlockedClassAction]);

  const handleKeyboardToggle = useCallback(() => {
    if (!featureFlags.keyboard) { showBlockedClassAction(); return; }
    if (!producerMode) setProducerMode(true);
    liveInput.setDockOpen(!liveInput.dockOpen);
  }, [featureFlags.keyboard, liveInput, producerMode, setProducerMode, showBlockedClassAction]);

  const handleLyricsToggle = useCallback(() => {
    if (!featureFlags.lyrics) { showBlockedClassAction(); return; }
    setLyricsOpen((open) => !open);
  }, [featureFlags.lyrics, showBlockedClassAction]);

  const handleLibraryOpen = useCallback((command = { tab: 'loops' }) => {
    if (!featureFlags.library) { showBlockedClassAction(); return; }
    setLibraryCollapsed(false);
    setLibraryCoachCommand({ ...command, requestId: Date.now() });
  }, [featureFlags.library, showBlockedClassAction]);

  const handleAutoArrangeOpen = useCallback(() => {
    if (!featureFlags.autoArrange) { showBlockedClassAction(); return; }
    setAutoArrangerOpen(true);
  }, [featureFlags.autoArrange, showBlockedClassAction]);

  useEffect(() => {
    if (!isClass || seatMode?.free || !classMode.focus) return;
    setProducerModeState(false);
    liveInput.setDockOpen(false);
    setMixerOpen(false);
    setEditingPatternId(null);
    setLyricsOpen(false);
    setAutoArrangerOpen(false);
  // Run only when the class Focus switch changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classMode.focus, isClass, seatMode?.free]);

  const draftTrackMix = useCallback((trackId, patch) => {
    const nextDrafts = { ...mixDraftsRef.current, [trackId]: { ...(mixDraftsRef.current[trackId] || {}), ...patch } };
    mixDraftsRef.current = nextDrafts;
    setMixDrafts(nextDrafts);
    updateMixGraph(tracksRef.current.map((track) => nextDrafts[track.id] ? mergeTrackMix(track, nextDrafts[track.id]) : track), bpmRef.current);
  }, []);

  const commitTrackMix = useCallback((trackId, patch = {}) => {
    if (!editableTracks(tracksRef.current).some((track) => track.id === trackId)) return;
    const finalPatch = { ...(mixDraftsRef.current[trackId] || {}), ...patch };
    const result = commitTrackMixState(pushState, { tracks: tracksRef.current, sectionMarkers }, trackId, finalPatch);
    const nextDrafts = { ...mixDraftsRef.current };
    delete nextDrafts[trackId];
    mixDraftsRef.current = nextDrafts;
    setMixDrafts(nextDrafts);
    updateMixGraph(result.tracks, bpmRef.current);
  }, [pushState, sectionMarkers]);

  // Play / Pause / Stop transport
  const handlePlayPause = useCallback(async () => {
    clearClassHighlight();
    if (liveInput.recording) { liveInput.stopRecording(); return; }
    if (getIsPlaying()) {
      pausePlayback();
      trackClassPlayback({ type: 'pause', bar: playheadBar });
      setIsPlaying(false);
      setIsPaused(true);
    } else if (getIsPaused()) {
      try {
        await ensureKnockBuffers(tracks, bpm);
        await ensurePatternBuffers(tracks, bpm).catch((error) => {
          console.warn('Pattern render failed:', error.message);
          if (!patternLoadNoticeShownRef.current) { patternLoadNoticeShownRef.current = true; setArrangementNotice('A pattern could not load its sounds'); }
        });
        startPlayback(tracks, bpm, totalBars, playheadBar, handlePlayheadUpdate, handlePlaybackEnd, { enabled: loopEnabled, region: loopRegion }, { metronome: liveInput.clickEnabled });
        trackClassPlayback({ type: 'play', bar: playheadBar });
        setIsPlaying(true);
        setIsPaused(false);
      } catch (e) {
        console.warn('Resume failed:', e.message);
        // Never leave audio running with the UI thinking it is stopped (frozen playhead, and the
        // next Space press would "pause" instead of play).
        stopPlayback();
        setIsPlaying(false);
        setIsPaused(false);
      }
    } else {
      // Stopped → ensure AudioContext is active (user gesture happening NOW) and buffers are loaded
      try {
        const { getAudioContext } = await import('@/audio/AudioEngine');
        const { getBuffer, hasCached } = await import('@/audio/BufferCache');
        const ctx = getAudioContext(); // Creates/resumes AudioContext (user gesture = valid)
        if (ctx.state === 'suspended') await ctx.resume();
        await ensureKnockBuffers(tracks, bpm);
        await ensurePatternBuffers(tracks, bpm).catch((error) => {
          console.warn('Pattern render failed:', error.message);
          if (!patternLoadNoticeShownRef.current) { patternLoadNoticeShownRef.current = true; setArrangementNotice('A pattern could not load its sounds'); }
        });

        // Check if any clip buffers are missing — if so, prefetch them now
        let needsFetch = false;
        for (const track of tracks) {
          if (!isAudioEligible(track)) continue;
          for (const clip of track.clips) {
            if (!hasCached(clip.loop_id) && !isRenderedClip(clip)) {
              needsFetch = true;
              break;
            }
          }
          if (needsFetch) break;
        }

        if (needsFetch) {
          console.log('[Playback] Fetching audio buffers before play...');
          const fetchPromises = [];
          for (const track of tracks) {
            if (!isAudioEligible(track)) continue;
            for (const clip of track.clips) {
              if (!hasCached(clip.loop_id) && !isRenderedClip(clip)) {
                // Use resolveAudioUrl which handles R2 → proxy routing for CORS
                // If clip has audio_url, cache it first so resolveAudioUrl picks it up
                if (clip.audio_url) cacheAudioUrl(clip.loop_id, clip.audio_url);
                const url = resolveAudioUrl(clip.loop_id, clip);
                if (url) {
                  fetchPromises.push(
                    getBuffer(clip.loop_id, url).catch(e => {
                      console.warn(`Buffer fetch failed for ${clip.loop_id}:`, e.message);
                      if (track.classSlot) setUnavailableTrackIds((current) => new Set([...current, track.id]));
                    })
                  );
                }
              }
            }
          }
          await Promise.all(fetchPromises);
        }

        startPlayback(tracks, bpm, totalBars, playheadBar, handlePlayheadUpdate, handlePlaybackEnd, { enabled: loopEnabled, region: loopRegion }, { metronome: liveInput.clickEnabled });
        trackClassPlayback({ type: 'play', bar: playheadBar });
        setIsPlaying(true);
        setIsPaused(false);
      } catch (e) {
        console.warn('Playback failed:', e.message);
        // Never leave audio running with the UI thinking it is stopped (frozen playhead, and the
        // next Space press would "pause" instead of play).
        stopPlayback();
        setIsPlaying(false);
        setIsPaused(false);
      }
    }
  }, [tracks, bpm, totalBars, playheadBar, handlePlayheadUpdate, handlePlaybackEnd, loopEnabled, loopRegion, liveInput, trackClassPlayback, clearClassHighlight]);

  const handleStop = useCallback(() => {
    if (liveInput.recording) liveInput.stopRecording({ stopTransport: false });
    stopPlayback();
    trackClassPlayback({ type: 'stop', bar: playheadBar });
    setIsPlaying(false);
    setIsPaused(false);
    if (patternLoopRestoreRef.current) {
      setLoopEnabled(patternLoopRestoreRef.current.enabled);
      setLoopRegion(patternLoopRestoreRef.current.region);
      patternLoopRestoreRef.current = null;
    }
    // playheadBar reset to 0 happens via stopPlayback→onPlayheadUpdate callback
  }, [liveInput, playheadBar, trackClassPlayback]);

  const finishNewProject = useCallback(() => {
    if (!featureFlags.newBeat) { showBlockedClassAction(); return; }
    setProjectId(null);
    localStorage.removeItem('beat_arranger_project_id');
    setProjectName('Untitled Beat');
    setBpm(90);
    setKeySignature(null);
    setTempoLockReleased(false);
    setTotalBars(32);
    suppressNextProjectDirtyRef.current = true;
    librarySessionRef.current += 1;
    resetHistory({ tracks: [], sectionMarkers: [] });
    setSelectedClipIds(new Set());
    setPlayheadBar(0);
    setProjectDirty(false);
    setEditingPatternId(null);
    setLyrics(emptyLyrics());
    setLyricsDirty(false);
    setLyricsImportOverflow(0);
    setLyricsUndoResetKey((value) => value + 1);
  }, [featureFlags.newBeat, resetHistory, showBlockedClassAction]);

  const resetGuestForNextStudent = useCallback(async () => {
    const currentGuestSessionId = guestSessionIdFromToken(guestToken);
    if (guestSaveTimer.current) clearTimeout(guestSaveTimer.current);
    guestSessionGuardRef.current.reset(); // invalidates any save already awaiting the API
    guestHydratedRef.current = false;
    lastAcknowledgedRef.current = null;
    guestDirtyRef.current = false;
    setGuestDirty(false);
    setLeavePrompt(false);
    stopPlayback();
    setIsPlaying(false);
    setIsPaused(false);
    librarySessionRef.current += 1;
    resetHistory({ tracks: [], sectionMarkers: [] });
    setSelectedClipIds(new Set());
    setPlayheadBar(0);
    setProjectName('Workshop Beat');
    setBpm(90);
    setKeySignature(null);
    setTotalBars(32);
    setTempoLockReleased(false);
    setEditingPatternId(null);
    clearCurrentGuestLyrics(localStorage, currentGuestSessionId);
    setLyrics(emptyLyrics());
    setLyricsDirty(false);
    setLyricsImportOverflow(0);
    setLyricsUndoResetKey((value) => value + 1);
    kioskTimerStartedAtRef.current = null;
    setKioskNow(Date.now());
    localStorage.removeItem('beat_arranger_guest_token');
    localStorage.removeItem('beat_arranger_guest_event');
    guestResetInProgressRef.current = true;
    setGuestToken(null);
    try {
      const result = await startGuestSession(eventName);
      localStorage.setItem('beat_arranger_guest_token', result.session_token);
      localStorage.setItem('beat_arranger_guest_event', eventName || '');
      setGuestToken(result.session_token); // hydration effect restores the new, empty session
    } catch (e) {
      console.warn('New workshop session failed:', e.message);
    } finally {
      guestResetInProgressRef.current = false;
    }
  }, [eventName, guestToken, resetHistory]);

  const requestNewBeat = useCallback(() => {
    if (!featureFlags.newBeat) { showBlockedClassAction(); return; }
    if (isGuest) { setNewBeatPrompt('guest'); return; }
    if (isAuthenticated && (projectDirty || lyricsDirty)) { setNewBeatPrompt('signed-in'); return; }
    setNewBeatPrompt('visitor');
  }, [featureFlags.newBeat, isGuest, isAuthenticated, projectDirty, lyricsDirty, showBlockedClassAction]);

  // Seek to bar (clicking ruler)
  const handleSeekToBar = useCallback((bar) => {
    clearClassHighlight();
    if (liveInput.recording) liveInput.stopRecording({ stopTransport: false });
    lastPasteEndBarRef.current = null; // Reset sequential paste on manual seek
    trackClassPlayback({ type: 'seek', bar });
    if (getIsPlaying()) {
      try {
        seekToBar(bar, tracks, bpm, totalBars, handlePlayheadUpdate, handlePlaybackEnd, { enabled: loopEnabled, region: loopRegion }, { metronome: liveInput.clickEnabled });
      } catch (e) {
        console.warn('Seek failed:', e.message);
      }
    } else {
      setPlayheadBar(bar);
    }
  }, [tracks, bpm, totalBars, handlePlayheadUpdate, handlePlaybackEnd, loopEnabled, loopRegion, liveInput, trackClassPlayback, clearClassHighlight]);

  // Rebuild the active schedule after edits or tempo changes. The scheduler
  // starts all sources up front, so this is what makes delete/move/add audible
  // immediately instead of at the next play.
  const playbackInputsRef = useRef({ tracks, bpm, totalBars, playheadBar, loopEnabled, loopRegion, clickEnabled: liveInput.clickEnabled });
  playbackInputsRef.current = { tracks, bpm, totalBars, playheadBar, loopEnabled, loopRegion, clickEnabled: liveInput.clickEnabled };
  const playbackReschedulerRef = useRef(null);
  const playbackRescheduleGenerationRef = useRef(0);
  if (!playbackReschedulerRef.current) {
    playbackReschedulerRef.current = createPlaybackRescheduler({
      isPlaying: () => getIsPlaying(),
      reschedule: async () => {
        // Knock clips keep one loop_id across feel/sound/variation/undo changes, so the
        // cached buffer must be re-rendered before the schedule is rebuilt from it.
        const generation = ++playbackRescheduleGenerationRef.current;
        try {
          await ensureKnockBuffers(playbackInputsRef.current.tracks, playbackInputsRef.current.bpm);
          await ensurePatternBuffers(playbackInputsRef.current.tracks, playbackInputsRef.current.bpm).catch((error) => {
            console.warn('Pattern re-render before reschedule failed:', error.message);
            if (!patternLoadNoticeShownRef.current) { patternLoadNoticeShownRef.current = true; setArrangementNotice('A pattern could not load its sounds'); }
          });
        } catch (e) {
          console.warn('Knock re-render before reschedule failed:', e.message);
        }
        if (generation !== playbackRescheduleGenerationRef.current || !getIsPlaying()) return;
        const current = playbackInputsRef.current;
        seekToBar(current.playheadBar, current.tracks, current.bpm, current.totalBars, handlePlayheadUpdate, handlePlaybackEnd, { enabled: current.loopEnabled, region: current.loopRegion }, { metronome: current.clickEnabled });
      },
    });
  }
  // Only the clip layout needs a rebuild. Volume, pan, mute and solo already apply
  // live through the scheduler's gain nodes; rebuilding for them restarted every
  // source on each fader move, which stutters.
  const arrangementShape = useMemo(() => arrangementSignature(tracks), [tracks]);
  useEffect(() => {
    playbackReschedulerRef.current.schedule();
  }, [arrangementShape, bpm, loopEnabled, loopRegion, liveInput.clickEnabled]);
  useEffect(() => () => playbackReschedulerRef.current?.cancel(), []);

  // Update mute/solo in scheduler when track states change during playback
  useEffect(() => {
    if (getIsPlaying()) {
      updateSoloMute(tracks);
    }
  }, [tracks]);

  // =====================================================================
  // AUDIO PREVIEW — real audio pipeline
  // Fetches audio via BufferCache (same pipeline as playback).
  // Creates AudioBufferSourceNode → masterGain → destination.
  // Does NOT create tracks or clips. Does NOT affect arrangement state.
  // Stops/interrupts cleanly when switching loops.
  // =====================================================================
  const handlePreviewLoop = useCallback(async (loop) => {
    if (!featureFlags.library) { showBlockedClassAction(); return; }
    // Stop any existing preview first
    if (previewSourceRef.current) {
      try { previewSourceRef.current.stop(); previewSourceRef.current.disconnect(); } catch (e) {}
      previewSourceRef.current = null;
    }

    setPreviewingLoopId(loop.id);

    try {
      const { getBuffer } = await import('@/audio/BufferCache');
      const { getAudioContext, getMasterGain } = await import('@/audio/AudioEngine');

      // Use direct SP2 audio URL if available, otherwise proxy
      const audioUrl = resolveAudioUrl(loop.id);
      const buffer = await getBuffer(loop.id, audioUrl);

      // Create preview source — direct to master gain, no track chain needed
      const ctx = getAudioContext();
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(getMasterGain());
      source.start(0);

      // Store reference for stop/interrupt
      previewSourceRef.current = source;

      // Auto-clear when finished
      source.onended = () => {
        if (previewSourceRef.current === source) {
          previewSourceRef.current = null;
          setPreviewingLoopId(null);
        }
      };
    } catch (e) {
      // Audio fetch/decode may fail for mock endpoints — degrade gracefully
      console.warn('Preview unavailable:', e.message);
      setPreviewingLoopId(null);
    }
  }, [featureFlags.library, showBlockedClassAction]);

  const handleStopPreview = useCallback(() => {
    if (previewSourceRef.current) {
      try { previewSourceRef.current.stop(); previewSourceRef.current.disconnect(); } catch (e) {}
    }
    previewSourceRef.current = null;
    setPreviewingLoopId(null);
  }, []);

  // =====================================================================
  // KNOCK ENGINE — Section 9.3
  // =====================================================================
  const handleKnockEngine = useCallback(async () => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    if (knockInFlight) return;
    const existingIndex = tracks.findIndex((track) => track.clips.some((clip) => clip.isKnockEngine));
    if (existingIndex === -1 && countTrackSlots(tracks, classSession) >= MAX_TRACKS) {
      console.warn(`Cannot add Knock Engine track: max ${MAX_TRACKS} tracks per project`);
      return;
    }
    if (existingIndex >= 0) { const clip = tracks[existingIndex].clips.find(c => c.isKnockEngine); if (!isEditableClip(tracks, clip.id)) { showBlockedClassAction(); return; } setMixerOpen(false); setEditingPatternId(clip.id); return; }
    setKnockInFlight(true);
    setArrangementNotice(null);
    try {
      const context = resolveKnockContext(tracks, keySignature);
      const pattern = regenerateKnockClip({ knockSoundType: 'Sub Sine', knockFeel: 'basic' }, context).knockPattern;
      const settings = soundSettings('Sub Sine');
      let nextTracks;

        const id = `knock-${Date.now()}-${tracks.length}`;
        const color = INSTRUMENT_COLORS.bass || 'var(--ba-class-bass)';
        nextTracks = [...tracks, { id: `track-${id}`, name: 'Knock Bass (Basic • Sub Sine)', instrument: 'bass', color, volume: 80, pan: 0, muted: false, solo: false,
          effects: defaultTrackEffects(), clips: [{ id: `clip-${id}`, loop_id: id, name: 'Knock Bass (Basic)', color, start_bar: snapToGrid(0), bars: 4,
            sourceBpm: bpm, naturalLoopBars: 4, isWholeLoop: true, fade_in: 0, fade_out: 0, audio_url: null, isKnockEngine: true, knockPattern: pattern,
            knockOscType: settings.oscType, knockFilterFreq: settings.filterFreq, knockDetune: settings.detune, knockVolume: 0.8, knockSoundType: 'Sub Sine',
            knockFeel: 'basic', knockKey: `${context.root} ${context.scaleType}`, knockGenre: context.genre }] }];
      pushState({ tracks: nextTracks, sectionMarkers });
      setMixerOpen(false); setEditingPatternId(nextTracks[nextTracks.length - 1].clips[0].id);
      await ensureKnockBuffers(nextTracks, bpm);
      await ensurePatternBuffers(nextTracks, bpm).catch((error) => console.warn('Pattern render after Knock update failed:', error.message));
      setArrangementNotice('Knock bass added. Draw notes or choose a sound.');
    } catch (e) {
      console.warn('Knock Engine error:', e.message);
      setArrangementNotice('Could not generate Knock bass. Try again.');
    } finally {
      setKnockInFlight(false);
    }
  }, [bpm, featureFlags.producer, keySignature, tracks, sectionMarkers, classSession, pushState, knockInFlight, showBlockedClassAction]);

  const handleKnockControl = useCallback(async (trackId, action, value) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    const clip = tracks.find(t => t.id === trackId)?.clips.find(c => c.isKnockEngine);
    if (clip && isEditableClip(tracks, clip.id)) { setMixerOpen(false); setEditingPatternId(clip.id); }
  }, [featureFlags.producer, tracks, showBlockedClassAction]);

  const handleCoachAction = useCallback((action) => {
    if (action === 'show-drums' || action === 'drums') {
      handleLibraryOpen({ tab: 'loops', instrument: 'drums' });
    } else if (action === 'show-melodies' || action === 'melodies') {
      handleLibraryOpen({ tab: 'loops', instrument: 'melody' });
    } else if (action === 'show-textures') {
      handleLibraryOpen({ tab: 'loops', instrument: 'texture' });
    } else if (action === 'show-fx') {
      handleLibraryOpen({ tab: 'loops', instrument: 'fx' });
    } else if (action === 'match-tempo') {
      if (!featureFlags.library) { showBlockedClassAction(); return; }
      setTempoLockReleased(false);
      handleLibraryOpen({ tab: 'loops' });
    } else if (action === 'packs') {
      handleLibraryOpen({ tab: 'packs' });
    } else if (action === 'add-knock') {
      handleKnockEngine();
    } else if (action === 'auto-arrange') {
      handleAutoArrangeOpen();
    } else if (action === 'add-section') {
      setSectionMarkerOpen(true);
    } else if (action === 'export' || action === 'send-beat') {
      openExport();
    }
  }, [featureFlags.library, handleAutoArrangeOpen, handleKnockEngine, handleLibraryOpen, openExport, showBlockedClassAction]);

  // =====================================================================
  // UNDOABLE OPERATIONS — all 15 call pushState()
  // =====================================================================

  // 1. Add track (from loop library click) — places clip at the current playhead
  const addTrack = useCallback(async (loop) => {
    if (!featureFlags.library) { showBlockedClassAction(); return; }
    const session = librarySessionRef.current;
    if (countTrackSlots(tracksRef.current, classSessionRef.current) >= MAX_TRACKS) {
      setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding.`); return;
    }
    if (loop.audio_url) cacheAudioUrl(loop.id, loop.audio_url);
    try {
      setArrangementNotice(`Loading full loop: ${loop.name}…`);
      const buffer = await getBuffer(loop.id, resolveAudioUrl(loop.id));
      if (session !== librarySessionRef.current) return;
      loop = fullLibraryLoop(loop, buffer.duration);
    } catch (error) {
      if (session === librarySessionRef.current) setArrangementNotice(`Could not add ${loop.name}: ${error.message}. Try again when the audio is available.`);
      return;
    }
    const current = librarySnapshotRef.current;
    const tracks = current.tracks;
    const sectionMarkers = current.sectionMarkers;
    if (countTrackSlots(tracks, current.classSession) >= MAX_TRACKS) {
      setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding.`); return;
    }
    const newClip = {
      id: `clip-${crypto.randomUUID()}`,
      loop_id: loop.id,
      name: loop.name,
      color: INSTRUMENT_COLORS[loop.instrument_group] || 'var(--ba-text-3)',
      start_bar: 0,
      bars: loop.bars,
      offset: 0,
      sourceBpm: loop.bpm,
      naturalLoopBars: loop.bars,
      sourceDurationSeconds: loop.sourceDurationSeconds,
      isWholeLoop: true,
      fade_in: 0,
      fade_out: 0,
      audio_url: loop.audio_url || null,
      style_tags: Array.isArray(loop.style_tags) ? loop.style_tags : [],
      key: loop.key || null,
    };
    // The first loop on an empty timeline sets the beat's tempo, as DAWs do. Without
    // this a new beat stayed at the default 90 and a kid's very first loop (often a
    // 120 or 70 BPM drum loop) was flagged as "at a different tempo".
    const loopBpm = Number(loop.bpm);
    const sessionBpm = tracks.length === 0 && Number.isFinite(loopBpm) && loopBpm >= 40 && loopBpm <= 220 ? loopBpm : bpmRef.current;
    if (sessionBpm !== bpmRef.current) setBpm(sessionBpm);
    bpmRef.current = sessionBpm;
    newClip.start_bar = snapClipStart(newClip, playheadBar, sessionBpm, snapToGrid);
    const newTrack = {
      id: `track-${crypto.randomUUID()}`,
      name: loop.name,
      instrument: loop.instrument_group,
      color: INSTRUMENT_COLORS[loop.instrument_group] || 'var(--ba-text-3)',
      volume: 80,
      pan: 0,
      muted: false,
      solo: false,
      effects: defaultTrackEffects(),
      clips: [newClip],
    };
    const next = { ...current, tracks: [...tracks, newTrack], sectionMarkers };
    librarySnapshotRef.current = next;
    tracksRef.current = next.tracks;
    pushState(next);
    setArrangementNotice(`${loop.name}: full ${Number(loop.bars.toFixed(2))}-bar loop added.`);
  }, [featureFlags.library, pushState, playheadBar, showBlockedClassAction]);

  // 2. Remove track
  const removeTrack = useCallback((trackId) => {
    if (!editableTracks(tracks).some((track) => track.id === trackId)) return;
    pushState({ tracks: tracks.filter((t) => t.id !== trackId), sectionMarkers });
    if (selectedTrackId === trackId) setSelectedTrackId(null);
  }, [tracks, sectionMarkers, pushState, selectedTrackId]);

  const renameTrack = useCallback((trackId, name) => {
    if (!editableTracks(tracks).some((track) => track.id === trackId)) return;
    pushState({ tracks: tracks.map((track) => track.id === trackId ? { ...track, name } : track), sectionMarkers });
  }, [tracks, sectionMarkers, pushState]);

  const duplicateTrack = useCallback((trackId = selectedTrackId, mode) => {
    if (!trackId) return;
    const result = commitDuplicateTrack(pushState, { tracks, sectionMarkers, classSession }, trackId, { maxTracks: MAX_TRACKS, classSession, lowEndLayer: mode === 'low-end' });
    if (result.reason === 'max-tracks') { setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before duplicating.`); return; }
    if (!result.duplicated) return;
    if (mode === 'low-end') setArrangementNotice('Low-end layer added at half volume. Open FX to shape the cutoff and blend it with the original.');
    setSelectedTrackId(result.duplicated.id);
    setSelectedClipIds(new Set(result.duplicated.clips.map((clip) => clip.id)));
  }, [selectedTrackId, tracks, sectionMarkers, classSession, pushState]);

  // 3. Add clip (place a loop on an existing track)
  const addClip = useCallback(async (trackId, loop, barPosition) => {
    if (!editableTracks(tracks).some((track) => track.id === trackId)) return;
    const session = librarySessionRef.current;
    try {
      if (loop.audio_url) cacheAudioUrl(loop.id, loop.audio_url);
      loop = fullLibraryLoop(loop, (await getBuffer(loop.id, resolveAudioUrl(loop.id))).duration);
    } catch (error) { setArrangementNotice(`Could not add ${loop.name}: ${error.message}`); return; }
    if (session !== librarySessionRef.current) return;
    const current = librarySnapshotRef.current;
    if (!editableTracks(current.tracks).some(track => track.id === trackId)) return;
    const newClip = {
      id: `clip-${crypto.randomUUID()}`,
      loop_id: loop.id,
      name: loop.name,
      color: INSTRUMENT_COLORS[loop.instrument_group] || 'var(--ba-text-3)',
      start_bar: 0,
      bars: loop.bars,
      sourceBpm: loop.bpm,
      naturalLoopBars: loop.bars,
      isWholeLoop: true,
      fade_in: 0,
      fade_out: 0,
      audio_url: loop.audio_url || null,
      style_tags: Array.isArray(loop.style_tags) ? loop.style_tags : [],
      key: loop.key || null,
    };
    newClip.start_bar = snapClipStart(newClip, barPosition, bpm, snapToGrid);
    const newTracks = current.tracks.map((t) =>
      t.id === trackId ? { ...t, clips: [...t.clips, newClip] } : t
    );
    const next = { ...current, tracks: newTracks };
    librarySnapshotRef.current = next; tracksRef.current = next.tracks; pushState(next);
  }, [tracks, pushState, bpm]);

  // 4. Remove clip
  const removeClip = useCallback((clipId) => {
    if (!isEditableClip(tracks, clipId)) return;
    const newTracks = tracks.map((t) => ({
      ...t,
      clips: t.clips.filter((c) => c.id !== clipId),
    }));
    pushState({ tracks: newTracks, sectionMarkers });
    setSelectedClipIds((prev) => { const next = new Set(prev); next.delete(clipId); return next; });
    clearClassHighlight();
    if (activeTool === 'Select') celebrateClassTool('Select');
  }, [tracks, sectionMarkers, pushState, activeTool, celebrateClassTool, clearClassHighlight]);

  // 5. Move clip (drag on grid — snapped)
  const moveClip = useCallback((clipId, newBarPosition) => {
    if (!isEditableClip(tracks, clipId)) return;
    const classEnd = flow2ClassEnd(classSession, bpm);
    const newTracks = tracks.map((t) => ({
      ...t,
      clips: t.clips.map((c) => {
        if (c.id !== clipId) return c;
        const snapped = Math.max(0, newBarPosition);
        return { ...c, start_bar: classEnd === null ? snapped : clampClipStartAtEnd(c, snapped, bpm, classEnd) };
      }),
    }));
    pushState({ tracks: newTracks, sectionMarkers });
    clearClassHighlight();
  }, [tracks, sectionMarkers, classSession, pushState, bpm, clearClassHighlight]);

  // 6. Slice clip (at snap point)
  const sliceClip = useCallback((clipId, sliceBar) => {
    if (!isEditableClip(tracks, clipId)) return;
    const snapped = snapToGrid(sliceBar);
    let changed = false;
    const newTracks = tracks.map((t) => {
      const idx = t.clips.findIndex((c) => c.id === clipId);
      if (idx === -1) return t;
      const clip = t.clips[idx];
      const endBar = clipEndBar(clip, bpm);
      if (snapped <= clip.start_bar || snapped >= endBar) return t;
      changed = true;
      const leftNativeBars = sessionBarsToNative(snapped - clip.start_bar, clip, bpm);
      const leftClip = { ...clip, bars: leftNativeBars, offset: (clip.offset||0)+(clip.reverse?clip.bars-leftNativeBars:0), isWholeLoop: false };
      const rightClip = {
        ...clip,
        id: `clip-${Date.now()}`,
        start_bar: snapped,
        bars: sessionBarsToNative(endBar - snapped, clip, bpm),
        offset: clip.reverse ? (clip.offset||0) : sliceRightOffset(clip, leftNativeBars),
        isWholeLoop: false,
      };
      const newClips = [...t.clips];
      newClips.splice(idx, 1, leftClip, rightClip);
      return { ...t, clips: newClips };
    });
    if (!changed) return;
    pushState({ tracks: newTracks, sectionMarkers });
    clearClassHighlight();
    celebrateClassTool('Slice');
  }, [tracks, sectionMarkers, pushState, bpm, celebrateClassTool, clearClassHighlight]);

  const availableNativeFor = useCallback((clip) => {
    if (clip.isPattern) return Number(clip.pattern?.patternBars || clip.naturalLoopBars || 1);
    const owner = tracks.find((track) => track.clips?.some((candidate) => candidate.id === clip.id));
    const buffer = isAudioEligible(owner) ? getCached(clip.loop_id) : null;
    // The cached buffer is the original audio, so measure it at its own tempo (Knock clips: the song tempo).
    if (buffer) return bufferNativeBars(clip, buffer, bpm);
    return clip.naturalLoopBars ?? (Number(clip.bars || 0) + Number(clip.offset || 0));
  }, [bpm, tracks]);

  const editingPatternClip = useMemo(() => tracks.flatMap((track) => track.clips || []).find((clip) => clip.id === editingPatternId && (clip.isPattern || clip.isKnockEngine)) || null, [tracks, editingPatternId]);
  useEffect(() => { if (editingPatternId && !editingPatternClip) setEditingPatternId(null); }, [editingPatternId, editingPatternClip]);

  // ---- Keyboard dock sound picker: switch what the keys play, or start a track with that sound ----
  const [dockSoundsReady, setDockSoundsReady] = useState(false);
  useEffect(() => {
    if (!liveInput.dockOpen || dockSoundsReady) return;
    let alive = true;
    loadProducerSounds().then(() => { if (alive) setDockSoundsReady(true); }).catch(() => {});
    return () => { alive = false; };
  }, [liveInput.dockOpen, dockSoundsReady]);
  // Preload so picking and stepping through sounds is instant: the first sound of every category when the dock
  // opens, then the whole category you are in (the live server can take a couple of seconds per file).
  const preloadedCategoriesRef = useRef(new Set());
  const preloadSounds = useCallback((sounds) => {
    sounds.reduce((chain, sound) => chain.then(() => getSoundBuffer(sound.id).catch(() => null)), Promise.resolve());
  }, []);
  useEffect(() => {
    if (!liveInput.dockOpen || !dockSoundsReady) return;
    preloadSounds(getOneShotCategories().map((category) => getOneShots(category.id)[0]).filter(Boolean));
  }, [liveInput.dockOpen, dockSoundsReady, preloadSounds]);
  const preloadCategory = useCallback((category) => {
    if (!category || preloadedCategoriesRef.current.has(category)) return;
    preloadedCategoriesRef.current.add(category);
    preloadSounds(getOneShots(category));
  }, [preloadSounds]);
  const dockCurrentSound = useMemo(() => currentChoiceForTrack(liveInput.armedTrack, { findSound, kits: getKits() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveInput.armedTrack, dockSoundsReady]);
  const pickDockSound = useCallback(async (choice) => {
    if (!featureFlags.keyboard || !featureFlags.producer) { showBlockedClassAction(); return; }
    if (!choice) return;
    if (liveInput.recording) { setArrangementNotice('Stop recording before you change the sound.'); return; }
    await loadProducerSounds();
    // Warm the sample now so the first key you press after picking it is not silent while it loads.
    if (choice.kind === 'sample' && choice.sound?.id) { getSoundBuffer(choice.sound.id).catch(() => {}); preloadCategory(choice.sound.category); }
    const kits = getKits();
    const armed = tracks.find((track) => track.id === liveInput.armedTrackId) || null;
    if (armed && choiceFitsTrack(choice, armed)) {
      pushState({ tracks: applyInstrumentChoice(tracks, armed.id, choice, { kits }), sectionMarkers });
      return;
    }
    // Nothing armed, or the armed track is the wrong kind (keys vs drums): start a track with this sound.
    if (countTrackSlots(tracks, classSession) >= MAX_TRACKS) { setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding a pattern.`); return; }
    const type = choiceType(choice);
    const instrument = type === 'drums' ? 'drums' : 'melody';
    const sampleDefaults = type === 'sampler' && choice.sound ? samplerDefaultsForSound(choice.sound) : null;
    const clip = createPatternClip({ type, kit: kits.find((kit) => kit.id === choice.id) || kits[0] || null, sampleDefaults, startBar: snapToGrid(playheadBar || 0), color: INSTRUMENT_COLORS[instrument] || 'var(--ba-text-3)' });
    const track = { id: `track-${crypto.randomUUID()}`, name: clip.name, instrument, color: clip.color, volume: 80, pan: 0, muted: false, solo: false, effects: defaultTrackEffects(), clips: [clip] };
    const nextTracks = applyInstrumentChoice([...tracks, track], track.id, choice, { kits });
    pushState({ tracks: nextTracks, sectionMarkers });
    liveInput.setArmedTrackId(track.id);
  }, [featureFlags.keyboard, featureFlags.producer, liveInput, tracks, sectionMarkers, classSession, pushState, playheadBar, preloadCategory, showBlockedClassAction]);
  const cycleDockSound = useCallback((direction) => {
    const current = dockCurrentSound || { kind: 'synth', id: SYNTH_VOICES[0].id, label: SYNTH_VOICES[0].label };
    pickDockSound(cycleChoice(current, direction, { kits: getKits(), oneShots: getOneShots }));
  }, [dockCurrentSound, pickDockSound]);
  const dockSound = useMemo(() => ({
    current: dockCurrentSound,
    synths: SYNTH_VOICES,
    categories: dockSoundsReady ? getOneShotCategories().filter((category) => getOneShots(category.id).length) : [],
    kits: dockSoundsReady ? getKits() : [],
    soundsFor: getOneShots,
    onPick: pickDockSound,
    onCycle: cycleDockSound,
  }), [dockCurrentSound, dockSoundsReady, pickDockSound, cycleDockSound]);

  const addPatternTrack = useCallback(async (type) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    if (countTrackSlots(tracks, classSession) >= MAX_TRACKS) {
      setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding a pattern.`);
      return;
    }
    await loadProducerSounds();
    const instrument = type === 'drums' ? 'drums' : 'melody';
    // A new sampler starts on the first sound of the first category instead of "Sound missing".
    const firstCategory = getOneShotCategories()[0]?.id;
    const firstSound = getOneShots(firstCategory)[0] || getOneShots()[0];
    const sampleDefaults = type === 'sampler' && firstSound ? samplerDefaultsForSound(firstSound) : null;
    const clip = createPatternClip({ type, kit: getKits()[0] || null, sampleDefaults, startBar: snapToGrid(playheadBar || 0), color: INSTRUMENT_COLORS[instrument] || 'var(--ba-text-3)' });
    const track = {
      id: `track-${crypto.randomUUID()}`,
      name: clip.name,
      instrument,
      color: clip.color,
      volume: 80,
      pan: 0,
      muted: false,
      solo: false,
      effects: defaultTrackEffects(),
      clips: [clip],
    };
    pushState({ tracks: [...tracks, track], sectionMarkers });
    setMixerOpen(false);
    liveInput.setArmedTrackId(track.id);
    setEditingPatternId(clip.id);
  }, [featureFlags.producer, tracks, sectionMarkers, classSession, pushState, playheadBar, liveInput, showBlockedClassAction]);

  const editPattern = useCallback((clipId) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    if (!isEditableClip(tracks, clipId)) return;
    const owner = tracks.find((track) => (track.clips || []).some((clip) => clip.id === clipId));
    if (owner) liveInput.setArmedTrackId(owner.id);
    setMixerOpen(false);
    setEditingPatternId(clipId);
  }, [featureFlags.producer, tracks, liveInput, showBlockedClassAction]);
  const commitPattern = useCallback((nextPattern) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    if (!editingPatternClip) return;
    let nextTracks = applyLinkedPattern(tracks, editingPatternClip.loop_id, editingPatternClip.patternType, nextPattern);
    if (editingPatternClip.patternType === 'keys') {
      const instrument = ['808', 'fingerbass'].includes(nextPattern.voice) ? 'bass' : 'melody';
      nextTracks = nextTracks.map((track) => (track.clips || []).some((clip) => clip.loop_id === editingPatternClip.loop_id) ? { ...track, instrument } : track);
    }
    pushState({ tracks: nextTracks, sectionMarkers });
  }, [editingPatternClip, featureFlags.producer, tracks, sectionMarkers, pushState, showBlockedClassAction]);
  const writeMelodyStarter = useCallback((generated, family, writeInto = false) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return false; }
    if (!editingPatternClip) return false;
    if (writeInto) {
      commitMelodyIntoClip(pushState, { tracks, sectionMarkers }, editingPatternClip, generated);
    } else {
      const result = commitStarterTrack(pushState, { tracks, sectionMarkers, classSession }, editingPatternClip, buildMelodyTrack(editingPatternClip, generated, family), { maxTracks: MAX_TRACKS });
      if (!result.ok) { setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding Melody.`); return false; }
    }
    setArrangementNotice('Chord tones land on the strong beats.');
    return true;
  }, [editingPatternClip, featureFlags.producer, pushState, sectionMarkers, classSession, tracks, showBlockedClassAction]);
  const writeBassStarter = useCallback((generated, followedKick = false) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return false; }
    if (!editingPatternClip) return false;
    const result = commitStarterTrack(pushState, { tracks, sectionMarkers, classSession }, editingPatternClip, buildBassTrack(editingPatternClip, generated), { maxTracks: MAX_TRACKS });
    if (!result.ok) { setArrangementNotice(`You can use up to ${MAX_TRACKS} tracks. Delete one before adding Bass.`); return false; }
    setArrangementNotice(`Bass follows the root of each chord${followedKick ? ' and your kick' : ''}.`);
    return true;
  }, [editingPatternClip, featureFlags.producer, pushState, sectionMarkers, classSession, tracks, showBlockedClassAction]);
  const makeUnique = useCallback((clipId) => {
    if (!featureFlags.producer) { showBlockedClassAction(); return; }
    if (!isEditableClip(tracks, clipId)) return;
    pushState({ tracks: makePatternUnique(tracks, clipId), sectionMarkers });
  }, [featureFlags.producer, tracks, sectionMarkers, pushState, showBlockedClassAction]);

  // Tempo changes re-render stretched loops in the background (debounced), so play starts without a wait.
  useEffect(() => {
    const timer = setTimeout(() => { ensureStretchedBuffers(tracks, bpm).catch(() => {}); }, 600);
    return () => clearTimeout(timer);
  }, [tracks, bpm]);

  /** Octave shift / time stretch for library loops: applies to the whole selection when the clip is selected. */
  const commitClipSound = useCallback((ids, patch) => {
    if (!featureFlags.clipSound) { showBlockedClassAction(); return; }
    const editableIds = new Set([...ids].filter((id) => isEditableClip(tracks, id)));
    if (!editableIds.size) return;
    const result = applyClipSound(tracks, editableIds, patch, bpm);
    if (!result.changed) return;
    const changedTracks = isExpo ? result.tracks.map((track) => ({ ...track, clips: track.clips.map((clip) => trimClipAtEnd(clip,bpm,expoBars(bpm))).filter(Boolean) })) : result.tracks;
    pushState({ tracks: changedTracks, sectionMarkers });
    // Start rendering now so the shifted or stretched audio is ready by the time you press play.
    ensureStretchedBuffers(result.tracks, bpm).catch(() => {});
  }, [featureFlags.clipSound, tracks, sectionMarkers, pushState, bpm, showBlockedClassAction, isExpo]);

  const changeClipSound = useCallback((clipId, patch) => {
    commitClipSound(selectedClipIds.has(clipId) ? selectedClipIds : new Set([clipId]), patch);
    clearClassHighlight();
  }, [selectedClipIds, commitClipSound, clearClassHighlight]);

  const closePatternEditor = useCallback(() => {
    if (patternLoopRestoreRef.current) handleStop();
    setEditingPatternId(null);
  }, [handleStop]);

  const toggleMixer = useCallback(() => {
    if (!featureFlags.mixer) { showBlockedClassAction(); return; }
    setMixerOpen((open) => {
      const next = !open;
      if (next) setEditingPatternId(null);
      return next;
    });
  }, [featureFlags.mixer, showBlockedClassAction]);

  const draftMasterVolume = useCallback((value) => {
    setMasterVolumeState(value);
    setMasterVolume(value / 100);
  }, []);

  const playEditingPattern = useCallback(async () => {
    if (!editingPatternClip) return;
    if (patternLoopRestoreRef.current && getIsPlaying()) { handleStop(); return; }
    if (getIsPlaying()) stopPlayback();
    if (!patternLoopRestoreRef.current) patternLoopRestoreRef.current = { enabled: loopEnabled, region: loopRegion };
    const region = { startBar: editingPatternClip.start_bar, endBar: clipEndBar(editingPatternClip, bpm) };
    setLoopRegion(region); setLoopEnabled(true); setPlayheadBar(region.startBar);
    try { await ensureKnockBuffers(tracks, bpm); } catch (error) { handleStop(); setArrangementNotice('Bass sound could not load. Choose another sound or try again.'); return; }
    await ensurePatternBuffers(tracks, bpm).catch((error) => {
      console.warn('Pattern preview render failed:', error.message);
      if (!patternLoadNoticeShownRef.current) { patternLoadNoticeShownRef.current = true; setArrangementNotice('A pattern could not load its sounds'); }
    });
    startPlayback(tracks, bpm, totalBars, region.startBar, handlePlayheadUpdate, handlePlaybackEnd, { enabled: true, region }, { metronome: liveInput.clickEnabled });
    setIsPlaying(true); setIsPaused(false);
  }, [editingPatternClip, tracks, bpm, totalBars, loopEnabled, loopRegion, handlePlayheadUpdate, handlePlaybackEnd, handleStop, liveInput.clickEnabled]);

  const trimClipEdge = useCallback((clipId, edge, targetBar, fine) => {
    if (!isEditableClip(tracks, clipId)) return;
    const newTracks = tracks.map((track) => ({ ...track, clips: track.clips.map((clip) => clip.id === clipId
      ? trimClip(clip, edge, targetBar, { bpm, availableNative: availableNativeFor(clip), fine, kiosk, pxPerBar: 52 * zoom })
      : clip) }));
    pushState({ tracks: newTracks, sectionMarkers });
  }, [tracks, sectionMarkers, pushState, bpm, availableNativeFor, kiosk, zoom]);

  const resetClip = useCallback((clipId) => {
    if (!isEditableClip(tracks, clipId)) return;
    const newTracks = tracks.map((track) => ({ ...track, clips: track.clips.map((clip) => clip.id === clipId
      ? resetClipLength(clip, availableNativeFor(clip)) : clip) }));
    pushState({ tracks: newTracks, sectionMarkers });
  }, [tracks, sectionMarkers, pushState, availableNativeFor]);

  // 7. Duplicate clip (placed after original, snapped)
  const duplicateClip = useCallback((clipId) => {
    if (!isEditableClip(tracks, clipId)) return;
    const classEnd = flow2ClassEnd(classSession, bpm);
    const owner = tracks.find((track) => track.clips.some((clip) => clip.id === clipId));
    const source = owner?.clips.find((clip) => clip.id === clipId);
    if (classEnd !== null && source && clipEndBar(source, bpm) >= classEnd - 1e-6) {
      // The library (where arrangement notices show) is hidden in class, so Pizzul says it.
      setClassCoachNotice({ kind: 'blocked', pose: 'thinking', line: 'That one already reaches the End.' });
      return;
    }
    let changed = false;
    const newTracks = tracks.map((t) => {
      const clip = t.clips.find((c) => c.id === clipId);
      if (!clip) return t;
      const maxBars = classEnd ?? BARS_RANGE.max;
      const dupBar = findCopyBar({ tracks, trackId: t.id, clip, bpm, mode: copySpacing, maxBars });
      if (dupBar === null) { if (classEnd === null) setArrangementNotice(`No room to duplicate: the song is at its ${BARS_RANGE.max}-bar limit.`); else setClassCoachNotice({ kind: 'blocked', pose: 'thinking', line: 'That one already reaches the End.' }); return t; }
      const bounded = classEnd === null
        ? { clip: { ...structuredClone(clip), start_bar: dupBar } }
        : copyClipAtEnd(clip, dupBar, bpm, classEnd);
      if (!bounded.clip) { setArrangementNotice(bounded.notice); return t; }
      const dup = { ...bounded.clip, id: `clip-${Date.now()}` };
      changed = true;
      return { ...t, clips: [...t.clips, dup] };
    });
    if (!changed) return;
    pushState({ tracks: newTracks, sectionMarkers });
    clearClassHighlight();
    celebrateClassTool('Copy');
  }, [tracks, sectionMarkers, classSession, pushState, bpm, copySpacing, celebrateClassTool, clearClassHighlight]);

  // 8. Paste clip (at playhead, snapped)
  const pasteClip = useCallback(() => {
    if (!clipboard) return;
    if (!editableTracks(tracks).some((track) => track.id === clipboard.trackId)) return;
    const classEnd = flow2ClassEnd(classSession, bpm);
    const raw = snapToGrid(playheadBar);
    const snapped = classEnd === null ? raw : clampClipStartAtEnd(clipboard.clip, raw, bpm, classEnd);
    const newClip = {
      ...structuredClone(clipboard.clip),
      id: `clip-${Date.now()}`,
      start_bar: snapped,
    };
    const newTracks = tracks.map((t) =>
      t.id === clipboard.trackId ? { ...t, clips: [...t.clips, newClip] } : t
    );
    pushState({ tracks: newTracks, sectionMarkers });
  }, [clipboard, playheadBar, tracks, sectionMarkers, classSession, pushState, bpm]);

  // 9-10. Volume, pan and effects preview live, then commit once per gesture.

  // 11. Change mute
  const changeMute = useCallback((trackId) => {
    const target = tracks.find((track) => track.id === trackId);
    if (target?.classSlot) {
      const result = toggleClassTrackMute(arrangement, trackId, MAX_TRACKS);
      if (result.blocked) { setArrangementNotice(result.notice); return; }
      if (!result.changed) return;
      pushState({ tracks: result.arrangement.tracks, classSession: result.arrangement.classSession, sectionMarkers });
      if (target.muted) {
        const promoted = result.arrangement.tracks.find((track) => track.id === trackId);
        const ready = Promise.all((promoted.clips || []).filter((clip) => !isRenderedClip(clip)).map((clip) => {
          const url = isAudioEligible(promoted) ? resolveAudioUrl(clip.loop_id, clip) : null;
          return url ? getBuffer(clip.loop_id, url) : Promise.resolve(null);
        })).then(() => {
          setUnavailableTrackIds((current) => { const next = new Set(current); next.delete(trackId); return next; });
          return ensureStretchedBuffers(result.arrangement.tracks, bpm);
        });
        // Unmuted mid-playback: the track had no audio scheduled, so rebuild once it loads.
        rescheduleWhenAudioReady(ready, { isPlaying: getIsPlaying, reschedule: () => playbackReschedulerRef.current?.schedule() }).catch((error) => {
          console.warn(`Class sound unavailable for ${trackId}:`, error.message);
          setUnavailableTrackIds((current) => new Set([...current, trackId]));
        });
      }
      return;
    }
    const newTracks = tracks.map((t) =>
      t.id === trackId ? { ...t, muted: !t.muted } : t
    );
    pushState({ tracks: newTracks, sectionMarkers });
  }, [tracks, sectionMarkers, pushState, arrangement, bpm]);

  const handleBuildMyBeat = useCallback(() => {
    if (classSession?.phase !== 'pick' || tracks.filter((track) => track.classSlot && !track.muted).length < 2) return;
    const next = expoMode ? buildExpoBeat(arrangement, bpm) : buildMyBeat(arrangement);
    pushState({ tracks: next.tracks, sectionMarkers: next.sectionMarkers, classSession: next.classSession });
    if (expoMode) { setTotalBars(expoBars(bpm)); setLoopEnabled(false); }
    if (next.classSession?.flow === 2) setTotalBars(classSongMap(bpm, 2, next.classSession.mapId).length);
    setSelectedClipIds(new Set());
  }, [arrangement, bpm, classSession, pushState, tracks, expoMode]);

  const handleAddClassMarkers = useCallback(() => {
    if (classSession?.phase !== 'arrange') return;
    clearClassHighlight();
    const nextMarkers = addClassMapMarkers(sectionMarkers, bpm, () => stableId('marker'), classSession.flow, classSession.mapId);
    if (nextMarkers.length === sectionMarkers.length) return;
    pushState({ tracks, sectionMarkers: nextMarkers });
  }, [bpm, classSession, pushState, sectionMarkers, tracks, clearClassHighlight]);

  const handleClassShowMe = useCallback((step) => {
    if (classSession?.flow !== 2) { setClassHighlight(step?.showMe || null); return; }
    const target = classShowMeTarget(step, tracks, bpm, classSession);
    if (step?.tool?.id) setActiveTool(step.tool.id);
    // Listening steps: put the playhead where the run has to start, so Play just works.
    if ((step?.id === 'hook' || step?.id === 'listen') && !getIsPlaying()) {
      const map = classSongMap(bpm, 2, classSession.mapId);
      const hookStart = map.sections.find((section) => section.name === 'Hook')?.startBar || 0;
      handleSeekToBar(step.id === 'hook' ? hookStart : 0);
    }
    setClassHighlight(target);
  }, [bpm, classSession, handleSeekToBar, tracks]);

  const handleSkipClassStep = useCallback((index) => {
    if (classSession?.phase !== 'arrange' || index !== furthestStep) return;
    if (classSession.flow === 2) {
      const step = classStepsForFlow(2)[index];
      if (!step) return;
      const skipped = Array.isArray(classSession.progress?.skipped) ? classSession.progress.skipped : [];
      if (skipped.includes(step.id)) return;
      // Skips are progress, not a music edit: keep them out of Undo like progress.done.
      updateState((current) => {
        const session = current.classSession;
        if (session?.flow !== 2) return current;
        const currentSkipped = Array.isArray(session.progress?.skipped) ? session.progress.skipped : [];
        if (currentSkipped.includes(step.id)) return current;
        return { ...current, classSession: { ...session, progress: { ...(session.progress || {}), skipped: [...currentSkipped, step.id] } } };
      });
      return;
    }
    setSkippedSteps((current) => new Set([...current, index]));
  }, [classSession, furthestStep, updateState]);

  const dismissClassCoach = useCallback(() => {
    setCoachController((current) => dismissCoachEvent(current));
    setClassCoachNotice(null);
  }, [setCoachController]);

  const toggleClassFocus = useCallback((focus) => {
    if (!classMode.canToggleFocus) return;
    setClassMode((current) => ({ ...current, focus: Boolean(focus) }));
  }, [classMode.canToggleFocus]);

  const handleSendToBank = useCallback((trackId) => {
    const result = sendTrackToBank(arrangement, trackId);
    if (!result.changed) return;
    pushState({ tracks: result.arrangement.tracks, sectionMarkers, classSession: result.arrangement.classSession });
    setSelectedClipIds((current) => {
      const ids = new Set(tracks.find((track) => track.id === trackId)?.clips?.map((clip) => clip.id) || []);
      return new Set([...current].filter((id) => !ids.has(id)));
    });
  }, [arrangement, pushState, sectionMarkers, tracks]);

  // 12. Change solo
  const changeSolo = useCallback((trackId) => {
    if (!editableTracks(tracks).some((track) => track.id === trackId)) return;
    const newTracks = tracks.map((t) =>
      t.id === trackId ? { ...t, solo: !t.solo } : t
    );
    pushState({ tracks: newTracks, sectionMarkers });
  }, [tracks, sectionMarkers, pushState]);

  // 13. Add section marker
  const addSectionMarker = useCallback((marker) => {
    pushState({ tracks, sectionMarkers: [...sectionMarkers, { ...marker, id: stableId('marker') }] });
  }, [tracks, sectionMarkers, pushState]);

  // 14. Remove section marker
  const removeSectionMarker = useCallback((index) => {
    const newMarkers = sectionMarkers.filter((_, i) => i !== index);
    pushState({ tracks, sectionMarkers: newMarkers });
  }, [tracks, sectionMarkers, pushState]);

  // 15. Auto arrange
  const autoArrange = useCallback((templateId) => {
    if (!featureFlags.autoArrange) { showBlockedClassAction(); return; }
    if (!templateId) return;
    const result = runAutoArrange(templateId, tracks, bpm);
    pushState({ tracks: result.tracks, sectionMarkers: ensureMarkerIds(result.sectionMarkers) });
    setTotalBars(result.totalBars);
  }, [featureFlags.autoArrange, tracks, pushState, bpm, showBlockedClassAction]);

  const addParsedSong = useCallback((parsed) => {
    if (!featureFlags.lyrics) { showBlockedClassAction(); return; }
    const incomingLines = parsed.sections.reduce((sum, section) => sum + section.lines.length, 0);
    const currentLines = lyrics.sections.reduce((sum, section) => sum + section.lines.length, 0);
    if (lyrics.sections.length + parsed.sections.length > 8 || currentLines + incomingLines > 48) {
      setArrangementNotice('That paste would exceed the lyrics limit. Remove a section or line first.');
      return;
    }
    let nextBar = sectionMarkers.length ? totalBars : 0;
    let overflow = 0;
    const addedMarkers = [];
    const addedSections = parsed.sections.map((section) => {
      const bars = sectionBars(section.lines.length);
      const sectionId = stableId('section');
      if (nextBar + bars > 80) {
        overflow += 1;
        return { ...section, id: sectionId, marker_id: null };
      }
      const markerId = stableId('marker');
      addedMarkers.push({
        id: markerId,
        name: section.label,
        startBar: nextBar,
        color: LYRICS_MARKER_COLOR,
      });
      nextBar += bars;
      return { ...section, id: sectionId, marker_id: markerId };
    });
    const nextLyrics = normalizeLyrics({
      ...lyrics,
      title: lyrics.title || parsed.title,
      style: lyrics.style || parsed.style,
      cadence: [...lyrics.cadence, ...parsed.cadence].slice(0, 7),
      sections: [...lyrics.sections, ...addedSections],
    });
    pushState({ tracks, sectionMarkers: [...sectionMarkers, ...addedMarkers] });
    setTotalBars(Math.max(16, Math.ceil(nextBar / 8) * 8));
    setLyricsImportOverflow((count) => count + overflow);
    handleLyricsChange(nextLyrics);
  }, [featureFlags.lyrics, handleLyricsChange, lyrics, pushState, sectionMarkers, totalBars, tracks, showBlockedClassAction]);

  // =====================================================================
  // NON-UNDOABLE — clipboard copy (no arrangement change)
  // Multi-select: stores all selected clips with relative offsets
  // =====================================================================
  const copySelectedClips = useCallback(() => {
    if (selectedClipIds.size === 0) return;
    const copied=makeSelectionClipboard(editableTracks(tracks),selectedClipIds,bpm,selectionRange,sectionMarkers);
    if(copied)setClipboard(copied);
    lastPasteEndBarRef.current=null;
  }, [selectedClipIds, tracks, bpm, selectionRange, sectionMarkers]);

  // Multi-paste with sequential advancement
  // First paste: at playheadBar. Subsequent: advances by selectionSpan.
  const pasteClips = useCallback(() => {
    if (!clipboard || !clipboard.items || clipboard.items.length === 0) return;

    if(clipboard.isSection) {
      const at=lastPasteEndBarRef.current ?? playheadBar;
      if(clipboard.markers?.length && sectionMarkers.some(m=>m.startBar>=at && m.startBar<at+clipboard.selectionSpan)){setArrangementNotice('That destination already has section markers. Choose empty measures, or use Duplicate after to insert room.');return;}
      const placed=placeSelection(tracks,clipboard,at,bpm,flow2ClassEnd(classSession,bpm)??BARS_RANGE.max,()=>stableId('clip'));
      if(!placed){setArrangementNotice('That section overlaps existing clips or reaches past the song limit. Choose empty measures.');return;}
      const nextLength=classSession?totalBars:Math.max(totalBars,Math.ceil(at+clipboard.selectionSpan));
      pushState({tracks:placed.tracks,sectionMarkers:[...sectionMarkers,...placed.markers],sectionSongLength:nextLength},{sectionSongLength:totalBars});
      setSelectedClipIds(new Set(placed.ids));setSelectionRange({startBar:at,endBar:at+clipboard.selectionSpan});
      lastPasteEndBarRef.current=at+clipboard.selectionSpan;return;
    }
    // Determine paste base position
    let baseBar;
    if (lastPasteEndBarRef.current !== null) {
      // Subsequent paste: advance from last paste end
      baseBar = snapToGrid(lastPasteEndBarRef.current);
    } else {
      // First paste: at playhead
      baseBar = snapToGrid(playheadBar);
    }

    // Never stack on top of existing clips: use the playhead if it is free, else the next empty
    // phrase boundary (8/4/2 bars by the copy's length). Accidental repeat pastes line up.
    const classEnd = flow2ClassEnd(classSession, bpm);
    const maxBars = classEnd ?? BARS_RANGE.max;
    baseBar = Math.min(baseBar, Math.max(0, maxBars - clipboard.selectionSpan));
    const placed = findPasteBar({ tracks, items: clipboard.items, desiredBar: baseBar, span: clipboard.selectionSpan, bpm, maxBars });
    if (placed === null) {
      setArrangementNotice(classEnd === null ? `No room to paste: the song is at its ${BARS_RANGE.max}-bar limit.` : 'No room to paste before the End.');
      return;
    }
    baseBar = placed;

    let newTracks = [...tracks.map((t) => ({ ...t, clips: [...t.clips] }))];
    const pastedIds = [];
    for (const item of clipboard.items) {
      if (!editableTracks(newTracks).some((track) => track.id === item.trackId)) continue;
      const newClip = {
        ...structuredClone(item.clip),
        id: `clip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        start_bar: baseBar + item.relativeOffset,
      };
      pastedIds.push(newClip.id);
      newTracks = newTracks.map((t) =>
        t.id === item.trackId ? { ...t, clips: [...t.clips, newClip] } : t
      );
    }
    pushState({ tracks: newTracks, sectionMarkers });
    setSelectedClipIds(new Set(pastedIds));

    // Advance lastPasteEndBar for next paste
    lastPasteEndBarRef.current = baseBar + clipboard.selectionSpan;
  }, [clipboard, playheadBar, tracks, sectionMarkers, classSession, pushState, bpm, totalBars]);

  const selectSection=useCallback(range=>{
    setSelectionRange(range);
    setSelectedClipIds(new Set(editableTracks(tracks).flatMap(t=>t.clips.filter(c=>c.start_bar<range.endBar && clipEndBar(c,bpm)>range.startBar).map(c=>c.id))));
  },[tracks,bpm]);
  const editClipAudio=useCallback((id,patch)=>{
    if(!isEditableClip(tracks,id) || seatMode?.readOnly)return;
    let changed=false;
    const next=tracks.map(t=>({...t,clips:t.clips.map(c=>{
      if(c.id!==id || c.isPattern)return c;
      const merged={...c,...patch};delete merged.fadeEnvelope;const f=clipFadeLengths(merged,timelineBars(c,bpm)*240/bpm);
      const result={...merged,fade_in:f.fadeIn,fade_out:f.fadeOut};
      changed=JSON.stringify(result)!==JSON.stringify(c);return result;
    })}));
    if(changed)pushState({tracks:next,sectionMarkers});
  },[tracks,sectionMarkers,pushState,bpm,seatMode]);
  const duplicateGroup=useCallback((omitted=new Set())=>{
    if(seatMode?.readOnly)return;
    const source=editableTracks(tracks).filter(t=>!omitted.has(t.id));
    const copied=makeSelectionClipboard(source,selectedClipIds,bpm,selectionRange,sectionMarkers);
    if(!copied)return;
    const at=selectionRange?.endBar??Math.max(...copied.items.map(i=>clipEndBar(i.clip,bpm)));
    const placed=insertSelection(tracks,sectionMarkers,copied,at,bpm,flow2ClassEnd(classSession,bpm)??BARS_RANGE.max,()=>stableId('clip'));
    if(!placed){setArrangementNotice('A clip crosses the insertion point, or the duplicate exceeds the song limit. Split that clip first, or paste into empty measures.');return;}
    const nextLength=classSession?totalBars:Math.max(totalBars,Math.ceil(at+copied.selectionSpan),...placed.tracks.flatMap(t=>t.inBank?[]:t.clips.map(c=>Math.ceil(clipEndBar(c,bpm)))));
    pushState({tracks:placed.tracks,sectionMarkers:placed.sectionMarkers,sectionSongLength:nextLength},{sectionSongLength:totalBars});
    setSelectedClipIds(new Set(placed.ids));setSelectionRange({startBar:at,endBar:at+copied.selectionSpan});
    setVariationOpen(false);
  },[tracks,selectedClipIds,bpm,selectionRange,sectionMarkers,classSession,pushState,seatMode,totalBars]);

  // Multi-duplicate: duplicate each selected clip after its original
  const duplicateSelectedClips = useCallback(() => {
    if (selectedClipIds.size === 0) return;
    if(selectionRange || selectedClipIds.size>1){duplicateGroup();return;}
    // Each copy goes right after its original when that space is empty, else to the next empty
    // phrase boundary on its track (earlier copies count, so duplicates never stack).
    let newTracks = tracks.map((t) => ({ ...t, clips: [...t.clips] }));
    let skipped = 0;
    for (const t of editableTracks(tracks)) {
      for (const clip of t.clips) {
        if (!selectedClipIds.has(clip.id)) continue;
        const at = findCopyBar({ tracks: newTracks, trackId: t.id, clip, bpm, mode: copySpacing, maxBars: flow2ClassEnd(classSession, bpm) ?? BARS_RANGE.max });
        if (at === null) { skipped += 1; continue; }
        const dup = { ...structuredClone(clip), id: `clip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, start_bar: at };
        newTracks = newTracks.map((nt) => nt.id === t.id ? { ...nt, clips: [...nt.clips, dup] } : nt);
      }
    }
    if (skipped) setArrangementNotice(`No room to duplicate ${skipped === 1 ? 'one clip' : `${skipped} clips`}: the song is at its ${BARS_RANGE.max}-bar limit.`);
    pushState({ tracks: newTracks, sectionMarkers });
  }, [selectedClipIds, tracks, sectionMarkers, classSession, pushState, bpm, copySpacing, selectionRange, duplicateGroup]);

  // Multi-delete: remove all selected clips in one undo push
  const deleteSelectedClips = useCallback(() => {
    if (selectedClipIds.size === 0) return;
    const newTracks = deleteSelectedEditableClips(tracks, selectedClipIds);
    pushState({ tracks: newTracks, sectionMarkers });
    setSelectedClipIds(new Set());
    clearClassHighlight();
    if (activeTool === 'Select') celebrateClassTool('Select');
  }, [selectedClipIds, tracks, sectionMarkers, pushState, activeTool, celebrateClassTool, clearClassHighlight]);

  // =====================================================================
  // SAVE / LOAD — real project persistence via API
  // =====================================================================
  const doSave = useCallback(async () => {
    if (isClass) return false;
    setSaveStatus('saving');
    try {
      const arrangementData = {
        tracks: tracks.map((t) => ({
          id: t.id, name: t.name, instrument: t.instrument, color: t.color,
          volume: t.volume, pan: t.pan, muted: t.muted, solo: t.solo,
          effects: t.effects, clips: t.clips,
        })),
      };
      const lyricsData = normalizeLyrics(lyrics);

      if (projectId) {
        await saveProject(projectId, {
          ...projectSessionFields({ projectName, bpm, totalBars, keySignature }),
          arrangement_data: arrangementData, section_markers: sectionMarkers,
          lyrics_data: lyricsData,
        });
      } else {
        const result = await createProject({
          name: projectName, bpm, total_bars: totalBars,
          arrangement_data: arrangementData,
          section_markers: sectionMarkers,
          lyrics_data: lyricsData,
        });
        const newId = result.id;
        setProjectId(newId);
        localStorage.setItem('beat_arranger_project_id', newId);
        await saveProject(newId, {
          ...projectSessionFields({ projectName, bpm, totalBars, keySignature }),
          arrangement_data: arrangementData,
          section_markers: sectionMarkers,
          lyrics_data: lyricsData,
        });
      }
      setSaveStatus('saved');
      setProjectDirty(false);
      setLyricsDirty(false);
      setTimeout(() => setSaveStatus(null), 2000);
      return true;
    } catch (e) {
      if (e.message === 'AUTH_REQUIRED') {
        setSaveStatus(null);
        setAuthModalContext('save');
        setAuthModalMessage('Sign in to save your beat');
        setAuthModalOpen(true);
        pendingSaveRef.current = true;
        return false;
      }
      console.warn('Save failed:', e.message);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      return false;
    }
  }, [isClass, projectId, projectName, bpm, totalBars, keySignature, tracks, sectionMarkers, lyrics]);

  const handleSave = useCallback(() => {
    if (isGuest || isClass) return;
    if (!isAuthenticated) {
      setAuthModalContext('save');
      setAuthModalMessage('Sign in to save your beat');
      setAuthModalOpen(true);
      pendingSaveRef.current = true;
      return;
    }
    doSave();
  }, [isGuest, isClass, isAuthenticated, doSave]);

  // Auth success handler — auto-retry pending actions
  const handleAuthSuccess = useCallback((authData) => {
    login(authData);
    if (pendingSaveRef.current) {
      pendingSaveRef.current = false;
      setTimeout(() => doSave(), 100);
    } else if (tracks.length > 0) {
      // Auto-save current beat after account creation (even if Save wasn't clicked)
      setTimeout(() => doSave(), 200);
    }
    if (authModalContext === 'projects') {
      setTimeout(() => setProjectsOpen(true), 100);
    }
    setAuthModalContext(null);
  }, [login, doSave, authModalContext, tracks.length]);

  // Auth-gated projects handler
  const handleOpenProjects = useCallback(() => {
    if (!featureFlags.openProjects) { showBlockedClassAction(); return; }
    if (!isAuthenticated) {
      setAuthModalContext('projects');
      setAuthModalMessage('Sign in to view your projects');
      setAuthModalOpen(true);
      return;
    }
    setProjectsOpen(true);
  }, [featureFlags.openProjects, isAuthenticated, showBlockedClassAction]);

  // Load project on mount if projectId exists AND no SP2 import
  useEffect(() => {
    if (isGuest || isClass || !projectId || hasImport.current) return;
    const doLoad = async () => {
      try {
        librarySessionRef.current += 1;
        const project = await loadProject(projectId);
        setTempoLockReleased(false);
        setProjectName(project.name);
        setBpm(project.bpm);
        setKeySignature(restoredKeySignature(project));
        setTotalBars(project.total_bars);
        const ad = project.arrangement_data || { tracks: [] };
        const sm = ensureMarkerIds(project.section_markers || []);
        const restoredTracks = normalizePatternTracks(ad.tracks || []);
        await ensureKnockBuffers(restoredTracks, project.bpm);
        await ensurePatternBuffers(restoredTracks, project.bpm).catch((error) => console.warn('Pattern load render failed:', error.message));
        suppressNextProjectDirtyRef.current = true;
        pushState({ tracks: restoredTracks, sectionMarkers: sm });
        setLyrics(safeLyrics(project.lyrics_data));
        setLyricsDirty(false);
        setLyricsImportOverflow(0);
        setLyricsUndoResetKey((value) => value + 1);
        setProjectDirty(false);
      } catch (e) {
        console.warn('Load failed:', e.message);
        // Clear stale projectId if load fails
        localStorage.removeItem('beat_arranger_project_id');
        setProjectId(null);
      }
    };
    doLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount

  // Load a different project (from Projects modal)
  const handleLoadProject = useCallback(async (pid) => {
    librarySessionRef.current += 1;
    if (!featureFlags.openProjects) { showBlockedClassAction(); return; }
    try {
      const project = await loadProject(pid);
      setTempoLockReleased(false);
      setProjectName(project.name);
      setBpm(project.bpm);
      setKeySignature(restoredKeySignature(project));
      setTotalBars(project.total_bars);
      setProjectId(pid);
      localStorage.setItem('beat_arranger_project_id', pid);
      const ad = project.arrangement_data || { tracks: [] };
      const sm = ensureMarkerIds(project.section_markers || []);
      const restoredTracks = normalizePatternTracks(ad.tracks || []);
      await ensureKnockBuffers(restoredTracks, project.bpm);
      await ensurePatternBuffers(restoredTracks, project.bpm).catch((error) => console.warn('Pattern load render failed:', error.message));
      suppressNextProjectDirtyRef.current = true;
      pushState({ tracks: restoredTracks, sectionMarkers: sm });
      setLyrics(safeLyrics(project.lyrics_data));
      setLyricsDirty(false);
      setLyricsImportOverflow(0);
      setLyricsUndoResetKey((value) => value + 1);
      setSelectedClipIds(new Set());
      setPlayheadBar(0);
      setProjectDirty(false);
    } catch (e) {
      console.warn('Load project failed:', e.message);
    }
  }, [featureFlags.openProjects, pushState, showBlockedClassAction]);

  // New project (from Projects modal)
  const handleNewProject = useCallback(() => {
    if (!featureFlags.newBeat) { showBlockedClassAction(); return; }
    finishNewProject();
  }, [featureFlags.newBeat, finishNewProject, showBlockedClassAction]);

  // =====================================================================
  // EXPORT — real client-side MP3 rendering via ExportRenderer
  // =====================================================================
  const handleExport = useCallback(async () => {
    if (!featureFlags.export) { showBlockedClassAction(); return; }
    setExportStatus('rendering');
    try {
      const mp3Blob = await renderToMp3(tracks, bpm, totalBars, () => {});
      const url = URL.createObjectURL(mp3Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (projectName || 'beat').replace(/\s+/g, '_') + '_' + bpm + 'bpm_' + new Date().toISOString().slice(0, 10).replace(/-/g, '') + '.mp3';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportStatus('success');
    } catch (e) {
      console.error('Export failed:', e);
      setExportStatus('error');
    }
  }, [featureFlags.export, tracks, bpm, totalBars, projectName, showBlockedClassAction]);

  const handleBpmChange = useCallback((value) => {
    if (isExpo) return;
    const nextBpm = Number(value);
    if (classMapCanRefresh(classSession)) {
      const mapId = selectClassSongMapId(nextBpm);
      updateState((current) => ({ ...current, classSession: { ...current.classSession, mapId } }));
      setTotalBars(classSongMap(nextBpm, 2, mapId).length);
    }
    setBpm(nextBpm);
    setProjectDirty(true);
  }, [classSession, updateState, isExpo]);

  const handleBarsChange = useCallback((value) => {
    if (isExpo) return;
    if (classSession?.flow === 2) {
      setTotalBars(classSongMap(bpm, 2, classSession.mapId).length);
      return;
    }
    setTotalBars(value);
    setProjectDirty(true);
  }, [bpm, classSession, isExpo]);

  // =====================================================================
  // KEYBOARD SHORTCUTS — all 15 (Section 11)
  // =====================================================================
  const shortcutHandlers = useMemo(() => ({
    onReturnToStart: () => handleSeekToBar(0),
    // 1. Space → Play/Pause
    onPlayPause: () => { handlePlayPause(); },
    onRecord: featureFlags.record && !seatMode?.readOnly ? () => { if (producerMode && !kiosk) handleRecord(); } : undefined,
    // 2. Cmd+Z → Undo
    onUndo: seatMode?.readOnly ? undefined : () => { undo();setSelectedClipIds(new Set());setSelectionRange(null); },
    // 3. Cmd+Shift+Z → Redo
    onRedo: seatMode?.readOnly ? undefined : () => { redo();setSelectedClipIds(new Set());setSelectionRange(null); },
    // 4. Cmd+C → Copy
    onCopy: seatMode?.readOnly ? undefined : () => { copySelectedClips(); },
    onSelectAll: seatMode?.readOnly ? undefined : () => { selectAllClips(); },
    // 5. Cmd+V → Paste
    onPaste: seatMode?.readOnly ? undefined : () => { pasteClips(); },
    // 6. Cmd+D → Duplicate
    onDuplicate: seatMode?.readOnly ? undefined : () => { duplicateSelectedClips(); },
    onDuplicateTrack: seatMode?.readOnly ? undefined : () => { duplicateTrack(); },
    // 7. Delete/Backspace → Delete
    onDelete: seatMode?.readOnly ? undefined : () => { deleteSelectedClips(); },
    onTranspose: seatMode?.readOnly ? undefined : (delta) => { if (selectedClipIds.size) commitClipSound(selectedClipIds, { pitchDelta: delta }); },
    // 8. S → Slice tool
    onSliceTool: seatMode?.readOnly ? undefined : () => { setActiveTool((t) => t === 'Slice' ? 'Select' : 'Slice'); },
    // 9. V → Select tool
    onSelectTool: seatMode?.readOnly ? undefined : () => { setActiveTool('Select'); },
    onToggleLoop: () => { toggleLoop(); },
    onShortcuts: () => { setShortcutsOpen((open) => !open); },
    onEscape: () => {
      if (liveInput.dockOpen) { liveInput.setDockOpen(false); return; }
      if (shortcutsOpen) { setShortcutsOpen(false); return; }
      if (!autoArrangerOpen && !exportOpen && !workshopExportOpen && !guestExportOpen && !projectsOpen && !sectionMarkerOpen && !authModalOpen && !newBeatPrompt) {setSelectedClipIds(new Set());setSelectionRange(null);}
    },
    // 10. Cmd+S → Save
    onSave: featureFlags.save ? () => { handleSave(); } : undefined,
    // 11. Cmd+E → Export modal
    onExport: (seatMode || isExpo) ? undefined : openExport,
    // 12. 1-9 → Jump to bar (1=bar 1 internally 0-based, so barNumber-1)
    onJumpToBar: (barNumber) => {
      const bar = Math.min(barNumber - 1, totalBars - 1);
      setPlayheadBar(Math.max(0, bar));
      lastPasteEndBarRef.current = null; // Reset sequential paste on manual move
    },
    // 13-14. Arrow keys → move playhead
    onMovePlayhead: (delta) => {
      setPlayheadBar((prev) => Math.max(0, Math.min(prev + delta, totalBars)));
      lastPasteEndBarRef.current = null; // Reset sequential paste on manual move
    },
    onZoom: (direction) => {
      changeZoom(direction);
    },
    suspendLetters: liveInput.dockOpen,
  // isGuest and isAuthenticated are read inside onExport. Without them here the
  // handler keeps first-render values, so a guest who signs in still gets the
  // guest export modal instead of the real export.
  }), [undo, redo, copySelectedClips, pasteClips, duplicateSelectedClips, duplicateTrack, deleteSelectedClips, selectedClipIds, commitClipSound, selectAllClips, toggleLoop, shortcutsOpen, autoArrangerOpen, exportOpen, workshopExportOpen, guestExportOpen, projectsOpen, sectionMarkerOpen, authModalOpen, newBeatPrompt, isExpo, handleSave, handlePlayPause, handleSeekToBar, totalBars, producerMode, kiosk, liveInput, handleRecord, featureFlags.record, featureFlags.save, openExport, changeZoom, seatMode]);

  const expoLocked = Boolean(isExpo && (expoHandoff || expoSaving || expoFilming || expoMode.busy));
  useKeyboardShortcuts(expoLocked ? {} : shortcutHandlers);

  const saveExpo = async () => {
    if (!expoMode || expoSavingRef.current || expoHandoff || classSession?.phase !== 'arrange') return;
    expoSavingRef.current = true; setExpoSaving(true); setExpoError(''); handleStop();
    try {
      const audible = tracks.filter((track) => !track.muted && !track.inBank);
      if (!audible.length) throw new Error('Turn on at least one sound before finishing.');
      // Fetch selected audio explicitly so a fast Finish click cannot publish a silent MP3
      // while background audition loading is still in flight.
      await Promise.all(audible.flatMap((track) => track.clips.map((clip) => getBuffer(clip.loop_id, resolveAudioUrl(clip.loop_id, clip)))));
      await ensureStretchedBuffers(audible, bpm);
      const blob = await renderToMp3(tracks, bpm, expoBars(bpm), () => {});
      if (!blob?.size) throw new Error('Could not render your beat. Try again.');
      const result = await expoMode.onExport(blob);
      setExpoHandoff(result);
    } catch (e) { setExpoError(e.message || 'Could not save your beat. Keep this screen open and retry.'); }
    finally { expoSavingRef.current = false; setExpoSaving(false); }
  };
  const playExpo = () => {
    setLoopEnabled(false); setExpoFilming(true);
    if (!isPlaying) { handleSeekToBar(0); handlePlayPause(); }
  };
  const expoControls = expoMode ? <ExpoControls mode={expoMode} phase={classSession?.phase}
    picked={tracks.filter((track) => track.classSlot && !track.muted).length}
    onBuild={handleBuildMyBeat} onPlay={playExpo} onSave={saveExpo} saving={expoSaving}
    handoff={expoHandoff} onNext={expoMode.onNext} error={expoError} filming={expoFilming}
    onCloseFilm={() => { handleStop(); setExpoFilming(false); }} isPlaying={isPlaying} onStop={handleStop}
    selectedClip={tracks.flatMap((track) => track.clips || []).find((clip) => selectedClipIds.has(clip.id))}
    onClipSound={(patch) => commitClipSound(selectedClipIds,patch)} /> : null;

  return (
    <div className={`beat-arranger${kiosk ? ' kiosk' : ''}${seatMode ? ' seat-mode' : ''}${seatMode?.readOnly ? ' seat-read-only' : ''}`} data-testid="beat-arranger-app" data-skin="playground" data-class-focus={isClass && !seatMode?.free ? classMode.focus : undefined}>
      <TopNav
        features={seatMode ? { ...featureFlags, export: false } : featureFlags}
        readOnly={Boolean(seatMode?.readOnly || (expoMode && (expoHandoff || expoSaving || expoMode.busy)))}
        classHighlight={classHighlight}
        isAdmin={['admin', 'ltbb_admin'].includes(user?.role)}
        projectName={projectName}
        bpm={bpm}
        totalBars={totalBars}
        keySignature={keySignature}
        playheadBar={playheadBar}
        isPlaying={isPlaying}
        isRecording={liveInput.recording}
        clickEnabled={liveInput.clickEnabled}
        countInEnabled={liveInput.countInEnabled}
        autoQuantize={liveInput.autoQuantize}
        recordSnap={liveInput.recordSnap}
        keyboardOpen={liveInput.dockOpen}
        mixerOpen={mixerOpen}
        loopEnabled={loopEnabled}
        selectedCount={selectedClipIds.size}
        hasClipboard={Boolean(clipboard?.items?.length)}
        producerMode={producerMode}
        kiosk={kiosk}
        onProducerMode={setProducerMode}
        onNewPattern={addPatternTrack}
        onRecord={handleRecord}
        onClickToggle={() => liveInput.setClickEnabled(!liveInput.clickEnabled)}
        onCountInToggle={() => liveInput.setCountInEnabled(!liveInput.countInEnabled)}
        onAutoQuantizeToggle={() => liveInput.setAutoQuantize(!liveInput.autoQuantize)}
        onKeyboardToggle={handleKeyboardToggle}
        onMixerToggle={toggleMixer}
        lyricsOpen={lyricsOpen}
        onLyricsToggle={handleLyricsToggle}
        shortcutsOpen={shortcutsOpen}
        saveStatus={saveStatus}
        onNameChange={(name) => { setProjectName(name); setProjectDirty(true); }}
        onBpmChange={handleBpmChange}
        onBarsChange={handleBarsChange}
        onKeyChange={(value) => { setKeySignature(value); setProjectDirty(true); }}
        onToggleLoop={toggleLoop}
        onLoopLibrary={() => handleLibraryOpen({ tab: 'loops' })}
        onShortcutsOpen={setShortcutsOpen}
        onUndo={()=>{undo();setSelectedClipIds(new Set());setSelectionRange(null);}}
        onRedo={()=>{redo();setSelectedClipIds(new Set());setSelectionRange(null);}}
        canUndo={canUndo}
        canRedo={canRedo}
        onSave={handleSave}
        onProjects={handleOpenProjects}
        onExport={expoMode ? saveExpo : openExport}
        onTakeHome={seatMode && (seatMode.free || featureFlags.export) ? () => setTakeHomeOpen(true) : null}
        onPlayPause={handlePlayPause}
        onStop={handleStop}
        onReturnToStart={() => handleSeekToBar(0)}
        onBackBar={() => handleSeekToBar(Math.max(0, playheadBar - 1))}
        onForwardBar={() => handleSeekToBar(Math.min(totalBars - 1, playheadBar + 1))}
        onNewBeat={requestNewBeat}
        isGuest={isGuest}
        isAuthenticated={isAuthenticated}
        userName={user?.display_name || user?.email?.split('@')[0]}
        onSignIn={() => { setAuthModalContext(null); setAuthModalMessage(null); setAuthModalOpen(true); }}
        onSignOut={logout}
        hideAccount={Boolean(seatMode || expoMode)}
        nameReadOnly={Boolean(seatMode || expoMode)}
        kioskTimerLabel={kioskTimer.remainingSeconds == null ? null : formatKioskRemaining(kioskTimer.remainingSeconds)}
      />

      {/^(Recording into|Notes past)/.test(arrangementNotice || '') && <div data-testid="transport-recording-status" role="status" style={{ padding: '4px 12px', color: 'var(--ba-class-melodies)', background: 'var(--ba-surface-1)', borderBottom: '1px solid var(--ba-line)', fontSize: 11 }}>{arrangementNotice}</div>}

      {tempoMismatchCount > 0 && (
        <div data-testid="tempo-mismatch-notice" role="status" style={{ padding: '6px 14px', fontSize: 12, color: 'var(--ba-record)', background: 'var(--ba-surface-1)', borderBottom: '1px solid var(--ba-line)' }}>
          {tempoMismatchCount} clip{tempoMismatchCount === 1 ? '' : 's'} may not align at the session tempo; check duration and end alignment.
        </div>
      )}

      {expoControls}
      <EditingContext.Provider value={{snap:snapMode,range:selectionRange,onSelectRange:seatMode?.readOnly?null:selectSection,onClipEdit:seatMode?.readOnly||!featureFlags.clipSound?null:editClipAudio,onBoxSelect:ids=>{if(!seatMode?.readOnly){setSelectionRange(null);setSelectedClipIds(ids);}}}}>
      <MainLayout
        readOnly={Boolean(seatMode?.readOnly || (expoMode && (expoHandoff || expoSaving || expoMode.busy)))}
        showLibrary={featureFlags.library}
        allowLyrics={featureFlags.lyrics}
        allowClipSound={featureFlags.clipSound}
        classHighlight={classHighlight}
        onTimelineResize={handleTimelineResize}
        libraryCollapsed={libraryCollapsed}
        onToggleLibrary={() => setLibraryCollapsed(!libraryCollapsed)}
        tracks={displayTracks}
        totalBars={totalBars}
        sectionMarkers={sectionMarkers}
        playheadBar={playheadBar}
        isPlaying={isPlaying}
        loopRegion={loopRegion}
        loopEnabled={loopEnabled}
        onLoopRegion={setLoopFromRuler}
        onToggleLoop={toggleLoop}
        onLoopClip={loopClip}
        onAddTrack={addTrack}
        onPreviewLoop={handlePreviewLoop}
        onStopPreview={handleStopPreview}
        previewingLoopId={previewingLoopId}
        onKnockEngine={handleKnockEngine}
        knockInFlight={knockInFlight}
        arrangementNotice={arrangementNotice === patternLoadMessage ? null : arrangementNotice}
        onKnockControl={handleKnockControl}
        sessionBpm={bpm}
        onSeekToBar={handleSeekToBar}
        onMute={changeMute}
        onSolo={changeSolo}
        onDelete={removeTrack}
        onRenameTrack={renameTrack}
        onMixDraft={draftTrackMix}
        onMixCommit={commitTrackMix}
        onSelectTrack={selectTrackClips}
        armedTrackId={liveInput.armedTrackId}
        onArmTrack={liveInput.setArmedTrackId}
        onDuplicateTrack={duplicateTrack}
        showTrackArms={!kiosk && featureFlags.record}
        producerMode={producerMode}
        activeTool={activeTool}
        selectedClipIds={selectedClipIds}
        onSelectClip={handleSelectClip}
        onMoveClip={moveClip}
        onSliceClip={sliceClip}
        onDuplicateClip={duplicateClip}
        onDeleteClip={removeClip}
        onTrimClipEdge={trimClipEdge}
        onResetClip={resetClip}
        onEditPattern={editPattern}
        onMakeUnique={makeUnique}
        onClipSound={changeClipSound}
        editRow={<EditRow hideEmptySelectionTools={Boolean(editingPatternClip)} canUndo={canUndo} canRedo={canRedo} onUndo={()=>{undo();setSelectedClipIds(new Set());setSelectionRange(null);}} onRedo={()=>{redo();setSelectedClipIds(new Set());setSelectionRange(null);}} activeTool={activeTool} selectedCount={selectedClipIds.size} hasClipboard={Boolean(clipboard?.items?.length)} zoom={zoom}
          onToolChange={(tool) => { clearClassHighlight(); setActiveTool(tool); }} onSelectAll={selectAllClips} onCopy={copySelectedClips} onPaste={pasteClips}
          onAddSection={() => setSectionMarkerOpen(true)} onAutoArrange={handleAutoArrangeOpen} showAutoArrange={featureFlags.autoArrange} classHighlight={classHighlight}
          snapMode={snapMode} onSnapChange={setSnapMode} selectionRange={selectionRange}
          onDuplicateSelection={()=>duplicateGroup()} onClearSelection={()=>{setSelectionRange(null);setSelectedClipIds(new Set());}}
          onVariation={()=>{setVariationOmitted(new Set());setVariationOpen(true);}}
          onZoom={changeZoom} copySpacing={copySpacing} onCopySpacingChange={setCopySpacing} onFitSong={classSession?.flow === 2 && classSession.phase === 'arrange' ? fitClassSong : null} />}
        classCoach={expoMode ? <div className="expo-coach" /> : classSession ? <ClassCoach phase={classSession.phase} flow={classSession.flow} tracks={tracks} stepIndex={furthestStep} skipped={classSession.progress?.skipped || []} event={coachController.event} notice={classCoachNotice}
          flyMeter={flyMeter} showcaseText={showcaseText}
          onDismiss={dismissClassCoach} onShowMe={handleClassShowMe} onSkip={handleSkipClassStep} /> : null}
        classControls={expoMode ? null : <ClassSessionControls tracks={tracks} classSession={classSession} onBuild={handleBuildMyBeat} bpm={bpm} onAddMarkers={handleAddClassMarkers}
          classHighlight={classHighlight}
          contextLabel={seatMode ? `Beat ${seatMode.slot}` : 'Class preview'}
          focus={classMode.focus} canToggleFocus={classMode.canToggleFocus} onToggleFocus={toggleClassFocus} />}
        classSession={classSession}
        onSendToBank={classSession?.phase === 'arrange' ? handleSendToBank : null}
        unavailableTrackIds={unavailableTrackIds}
        bottomPanel={featureFlags.mixer && mixerOpen ? <MixerPanel tracks={displayTracks} isPlaying={isPlaying} producerMode={producerMode} masterVolume={masterVolume}
          onMasterDraft={draftMasterVolume} onMasterCommit={draftMasterVolume} onMute={changeMute} onSolo={changeSolo}
          onMixDraft={draftTrackMix} onMixCommit={commitTrackMix} onClose={() => setMixerOpen(false)} /> : featureFlags.producer && editingPatternClip?.isKnockEngine ? <BassEditor
          key={editingPatternClip.id}
          value={{ pattern: editingPatternClip.knockPattern, bars: editingPatternClip.naturalLoopBars || 4, sampleId: editingPatternClip.knockSampleId, soundType: editingPatternClip.knockSoundType, oscType: editingPatternClip.knockOscType, filterFreq: editingPatternClip.knockFilterFreq, detune: editingPatternClip.knockDetune, volume: editingPatternClip.knockVolume ?? .8, lockToKey: editingPatternClip.knockLockToKey }}
          bpm={bpm} keySignature={editingPatternClip.knockKey || keySignature}
          getContext={getAudioContext} destination={getMasterGain}
          onUndoEdit={undo} onRedoEdit={redo} canUndoEdit={canUndo} canRedoEdit={canRedo}
          onStopArrangement={handleStop} onListen={playEditingPattern} onClose={closePatternEditor}
          onGenerate={() => regenerateKnockClip(editingPatternClip, { ...resolveKnockContext([], editingPatternClip.knockKey || keySignature), genre: editingPatternClip.knockGenre || 'default' }).knockPattern}
          onChange={(value) => {
            if (!featureFlags.producer || !isEditableClip(tracks, editingPatternClip.id)) return;
            const settings = soundSettings(value.soundType);
            pushState({ sectionMarkers, tracks: tracks.map(t => ({ ...t, ...(t.clips.some(c => c.id === editingPatternClip.id) && /^Knock Bass/.test(t.name) ? { name: `Knock Bass (${value.soundType})` } : {}), clips: t.clips.map(c => c.id === editingPatternClip.id ? { ...c, name: /^Knock Bass/.test(c.name) ? `Knock Bass (${value.soundType})` : c.name, loop_id: `knock-${crypto.randomUUID()}`, knockPattern: value.pattern, knockSampleId: value.sampleId || null, knockSoundType: value.soundType, knockOscType: settings.oscType, knockFilterFreq: settings.filterFreq, knockDetune: settings.detune, knockLockToKey: value.lockToKey, reverse: false } : c) })) });
          }}
        /> : featureFlags.producer && editingPatternClip ? <PatternEditor
          clip={editingPatternClip}
          tracks={tracks}
          bpm={bpm}
          keySignature={keySignature}
          playheadBar={playheadBar}
          isPlaying={isPlaying}
          onClose={closePatternEditor}
          onCommitPattern={commitPattern}
          onMakeUnique={() => makeUnique(editingPatternClip.id)}
          onPlayPattern={playEditingPattern}
          onStopArrangement={handleStop}
          onKeySignatureChange={(value) => { setKeySignature(value); setProjectDirty(true); }}
          recording={liveInput.recording}
          recordingClipId={liveInput.recordingClipId}
          liveOctave={liveInput.octave}
          onOctaveChange={liveInput.setOctave}
          onAuditionNoteOn={liveInput.noteOn}
          onAuditionNoteOff={liveInput.noteOff}
          onWriteMelody={writeMelodyStarter}
          onWriteBass={writeBassStarter}
        /> : null}
        kiosk={kiosk}
        pxPerBar={52 * zoom}
        coachStep={kioskTimer.phase === 'nudge'
          ? { pose: 'writing', tip: '2 minutes left, time to send your beat!', action: 'send-beat', actionLabel: 'Send my beat' }
          : kioskTimer.phase === 'expired'
            ? { pose: 'writing', tip: "Time's up! Send your beat so the next person can go." }
            : currentCoachStep}
        onCoachAction={handleCoachAction}
        coachCommand={libraryCoachCommand}
        tempoLock={tempoLock}
        onReleaseTempoLock={() => setTempoLockReleased(true)}
        onRelockTempo={() => setTempoLockReleased(false)}
        lyricsOpen={lyricsOpen}
        onCloseLyrics={() => setLyricsOpen(false)}
        lyrics={lyrics}
        onLyricsChange={handleLyricsChange}
        onAddParsedSong={addParsedSong}
        lyricsImportOverflow={lyricsImportOverflow}
        lyricsUndoResetKey={lyricsUndoResetKey}
        aiDisabledCaption={seatMode?.aiDisabledCaption}
      />

      </EditingContext.Provider>

      {variationOpen && <div className="ba-variation-backdrop"><div className="ba-variation" role="dialog" aria-modal="true" aria-label="Duplicate with variation" onKeyDown={e=>{if(e.key==='Escape'){e.stopPropagation();setVariationOpen(false);}if(e.key==='Tab'){const items=e.currentTarget.querySelectorAll('button,input:not([disabled])');const first=items[0],last=items[items.length-1];if(e.shiftKey&&document.activeElement===first){e.preventDefault();last.focus();}else if(!e.shiftKey&&document.activeElement===last){e.preventDefault();first.focus();}}}}>
        <h2>Duplicate with variation</h2><p>Choose the tracks to include. The section keeps its full measure length.</p>
        {editableTracks(tracks).filter(t=>t.clips.some(c=>selectedClipIds.has(c.id))).map(t=><label key={t.id}><input type="checkbox" checked={!variationOmitted.has(t.id)} onChange={()=>setVariationOmitted(old=>{const next=new Set(old);if(next.has(t.id))next.delete(t.id);else next.add(t.id);return next;})}/>{t.name}</label>)}
        <button autoFocus onClick={()=>setVariationOpen(false)}>Cancel</button><button disabled={!tracks.some(t=>!variationOmitted.has(t.id)&&t.clips.some(c=>selectedClipIds.has(c.id)))} onClick={()=>duplicateGroup(variationOmitted)}>Duplicate variation</button>
      </div></div>}
      <KeyboardDock open={featureFlags.keyboard && producerMode && !kiosk && liveInput.dockOpen} armedType={liveInput.armedType} octave={liveInput.octave}
        velocityLevel={liveInput.velocityLevel} heldKeys={liveInput.heldKeys} midiSupported={liveInput.midiSupported} midiName={liveInput.midiName}
        onConnectMidi={liveInput.connectMidi} onNoteOn={liveInput.noteOn} onNoteOff={liveInput.noteOff} onClose={() => liveInput.setDockOpen(false)} sound={dockSound} />

      {/* StatusBar: playheadBar is 0-based, display as 1-based */}
      <StatusBar
        trackCount={tracks.length}
        tracks={tracks}
        classSession={classSession}
        currentBar={Math.floor(playheadBar) + 1}
        activeTool={activeTool}
      />

      {/* Save feedback at every screen width (the Save button's word label is hidden on most screens). */}
      {saveStatus && (
        <div data-testid="save-toast" role="status" style={{ position: 'fixed', top: 74, right: 16, zIndex: 90, padding: '8px 14px', borderRadius: 20, fontSize: 13, fontWeight: 700, boxShadow: 'var(--ba-shadow-popover)',
          background: saveStatus === 'error' ? 'var(--ba-record)' : saveStatus === 'saved' ? 'var(--ba-accent)' : 'var(--ba-surface-2)',
          color: saveStatus === 'saving' ? 'var(--ba-text)' : 'var(--ba-surface-0)', border: saveStatus === 'saving' ? '1px solid var(--ba-line)' : 'none' }}>
          {saveStatus === 'saving' ? 'Saving your beat...' : saveStatus === 'saved' ? 'Beat saved ✓' : "Couldn't save. Check your internet and try again."}
        </div>
      )}
      {arrangementNotice === patternLoadMessage && <div data-testid="pattern-load-toast" role="status" style={{ position: 'fixed', right: 16, bottom: 48, zIndex: 90, padding: '9px 14px', borderRadius: 18, background: 'var(--ba-surface-2)', color: 'var(--ba-text)', border: '1px solid var(--ba-record)', boxShadow: 'var(--ba-shadow-popover)', fontSize: 12 }}>{patternLoadMessage}</div>}
      {pendingLyricsBanner && (
        <div className="pending-lyrics-banner" role="status">
          <span>{pendingLyricsBanner === 'saving'
            ? 'Your lyrics are still on this device. Saving them to your beat...'
            : 'Lyrics from a guest session are on this device.'}</span>
          {pendingLyricsBanner === 'local-only' && <div>
            <button onClick={() => {
              const pending = readPendingLyrics(localStorage);
              if (pending?.lyrics) downloadLyricsText(safeLyrics(pending.lyrics));
            }}>Download .txt</button>
            <button onClick={() => {
              const pending = readPendingLyrics(localStorage);
              clearCurrentGuestLyrics(localStorage, pending?.guestSessionId);
              clearPendingLyrics(localStorage);
              setPendingLyricsBanner(null);
            }}>Delete</button>
          </div>}
        </div>
      )}
      <PizzulGuide isOpen={shortcutsOpen} onClose={() => setShortcutsOpen(false)} touchOnly={typeof window !== 'undefined' && Boolean(window.matchMedia?.('(hover: none) and (pointer: coarse)')?.matches)} />
      <AutoArrangerModal isOpen={featureFlags.autoArrange && autoArrangerOpen} onClose={() => setAutoArrangerOpen(false)} onSelectTemplate={autoArrange} />
      <ExportModal isOpen={featureFlags.export && exportOpen} onClose={() => { setExportOpen(false); setExportStatus(null); }} onExport={handleExport} exportStatus={exportStatus} projectName={projectName} bpm={bpm} totalBars={totalBars} trackCount={tracks.length} />
      {seatMode && <TakeHomeModal
        open={takeHomeOpen}
        slot={seatMode.slot}
        classCode={seatMode.classCode}
        onClose={() => setTakeHomeOpen(false)}
        onCreate={() => seatMode.createTakeHome(() => renderToMp3(tracks, bpm, totalBars, () => {}))}
        onDownload={() => seatMode.downloadBeat(() => renderToMp3(tracks, bpm, totalBars, () => {}))}
        onShow={seatMode.showTakeHome}
      />}
      <GuestExportModal isOpen={guestExportOpen} onClose={() => setGuestExportOpen(false)} onExport={handleGuestExport} isExporting={isExporting} kiosk={kiosk} onNextStudent={resetGuestForNextStudent} />
      <WorkshopExportModal isOpen={workshopExportOpen} onClose={() => setWorkshopExportOpen(false)} onExport={async (filename) => {
        if (!featureFlags.export) { showBlockedClassAction(); return; }
        const mp3Blob = await renderToMp3(tracks, bpm, totalBars, () => {});
        const url = URL.createObjectURL(mp3Blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }} onOpenAuth={() => { setAuthModalContext(null); setAuthModalMessage(null); setAuthModalOpen(true); }} projectName={projectName} bpm={bpm} />
      <ProjectsModal isOpen={projectsOpen} onClose={() => setProjectsOpen(false)} onLoadProject={handleLoadProject} onNewProject={handleNewProject} currentProjectId={projectId} />
      <SectionMarkerModal
        isOpen={sectionMarkerOpen}
        onClose={() => setSectionMarkerOpen(false)}
        onAddMarker={addSectionMarker}
        totalBars={totalBars}
        initialStartBar={Math.floor(playheadBar)}
      />
      <AuthModal isOpen={authModalOpen} onClose={() => { setAuthModalOpen(false); pendingSaveRef.current = false; }} onSuccess={handleAuthSuccess} contextMessage={authModalMessage} />
      {importPrompt && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'var(--ba-overlay)' }}
        >
          <div className="import-dialog">
            <h2>Open the song from The Booth?</h2>
            <p>It replaces what&apos;s on screen.</p>
            <div>
              <button onClick={() => {
                clearPendingImport();
                pendingImportRef.current = null;
                setImportPrompt(null);
              }}>Cancel</button>
              <button onClick={() => {
                const payload = importPrompt;
                setImportPrompt(null);
                applyImport(payload);
              }}>Replace without saving</button>
              {!isGuest && isAuthenticated && <button onClick={async () => {
                if (!await doSave()) return;
                const payload = importPrompt;
                setImportPrompt(null);
                applyImport(payload);
              }}>Save first, then open</button>}
            </div>
          </div>
        </div>
      )}
      {importError && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ background: 'var(--ba-overlay)' }}
        >
          <div className="import-dialog">
            <h2>Couldn&apos;t open that song</h2>
            <div>
              <button onClick={() => {
                clearPendingImport();
                pendingImportRef.current = null;
                setImportError(null);
              }}>Cancel</button>
              <button onClick={() => applyImport(pendingImportRef.current)}>Try again</button>
            </div>
          </div>
        </div>
      )}
      {newBeatPrompt && (
        <div data-testid="new-beat-dialog" className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'var(--ba-overlay)' }}>
          <div style={{ width: 420, background: 'var(--ba-surface-1)', border: '1px solid var(--ba-line)', borderRadius: 16, padding: 28 }}>
            <h2 style={{ color: 'var(--ba-text)', fontSize: 18, fontWeight: 700, marginBottom: 10 }}>
              {newBeatPrompt === 'guest' ? 'Send this beat to a grown-up\'s email before starting fresh?' : newBeatPrompt === 'signed-in' ? 'Save this beat first?' : 'Start a new beat?'}
            </h2>
            {newBeatPrompt === 'visitor' && <p style={{ color: 'var(--ba-text-2)', marginBottom: 20 }}>This clears the timeline.</p>}
            {newBeatPrompt === 'signed-in' && newBeatSaveError && <p data-testid="new-beat-save-error" style={{ color: 'var(--ba-record)', marginBottom: 16 }}>That save didn&apos;t go through, so your beat is still here. Try again, or choose Don&apos;t save.</p>}
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setNewBeatPrompt(null); setNewBeatSaveError(false); }} style={{ color: 'var(--ba-text)', background: 'var(--ba-surface-2)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Cancel</button>
              {newBeatPrompt === 'guest' ? <>
                <button onClick={() => { setNewBeatPrompt(null); setGuestExportOpen(true); }} style={{ color: 'var(--ba-text)', background: 'var(--ba-surface-2)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Send</button>
                <button onClick={() => { setNewBeatPrompt(null); resetGuestForNextStudent(); }} style={{ color: 'var(--ba-surface-0)', background: 'var(--ba-accent)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Start fresh</button>
              </> : newBeatPrompt === 'signed-in' ? <>
                <button onClick={() => { setNewBeatPrompt(null); finishNewProject(); }} style={{ color: 'var(--ba-text)', background: 'var(--ba-surface-2)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Don&apos;t save</button>
                <button onClick={async () => {
                  // Clear the timeline only after a confirmed save, so a failed save
                  // (offline, signed out) never throws the beat away.
                  setNewBeatSaveError(false);
                  if (await doSave()) { setNewBeatPrompt(null); finishNewProject(); } else setNewBeatSaveError(true);
                }} style={{ color: 'var(--ba-surface-0)', background: 'var(--ba-accent)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Save</button>
              </> : <button onClick={() => { setNewBeatPrompt(null); finishNewProject(); }} style={{ color: 'var(--ba-surface-0)', background: 'var(--ba-accent)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Start new beat</button>}
            </div>
          </div>
        </div>
      )}
      {leavePrompt && (
        <div data-testid="guest-leave-guard" className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'var(--ba-overlay)' }}>
          <div style={{ width: 420, background: 'var(--ba-surface-1)', border: '1px solid var(--ba-line)', borderRadius: 16, padding: 28 }}>
            <h2 style={{ color: 'var(--ba-text)', fontSize: 18, fontWeight: 700, marginBottom: 10 }}>Leave your beat?</h2>
            <p style={{ color: 'var(--ba-text-2)', fontSize: 14, lineHeight: 1.5, marginBottom: 20 }}>
              {leaveSaveTimedOut
                ? 'Your last save did not finish in time. Leaving now may lose your newest changes.'
                : 'We will save your newest changes before you leave.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setLeavePrompt(false)} style={{ color: 'var(--ba-text)', background: 'var(--ba-surface-2)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Keep editing</button>
              {leaveSaveTimedOut
                ? <button data-testid="guest-leave-anyway" onClick={() => leaveWorkshop(true)} style={{ color: 'var(--ba-surface-0)', background: 'var(--ba-record)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Leave anyway</button>
                : <button data-testid="guest-save-and-leave" onClick={() => leaveWorkshop(false)} style={{ color: 'var(--ba-surface-0)', background: 'var(--ba-accent)', borderRadius: 20, padding: '9px 16px', border: 'none' }}>Save and leave</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Workshop page: reads ?event= param, renders BeatArranger in guest mode
function WorkshopPage() {
  const [searchParams] = useSearchParams();
  const eventName = searchParams.get('event') || 'workshop';
  const { kiosk, timerMinutes } = parseKioskParams(window.location.search);
  return <BeatArranger isGuest={true} eventName={eventName} kiosk={kiosk} timerMinutes={timerMinutes} />;
}

// Facilitator dashboard page
function FacilitatorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const eventName = searchParams.get('event') || null;
  return <FacilitatorDashboard eventName={eventName} onBack={() => navigate('/')} />;
}

// Student dashboard
function DashboardPage() {
  const navigate = useNavigate();
  return <StudentDashboard onBack={() => navigate('/')} onEditBeat={(id) => { localStorage.setItem('beat_arranger_project_id', id); navigate('/'); }} />;
}

// Listen page (public, no auth)
function ListenPageRoute() {
  const { slug } = useParams();
  return <ListenPage slug={slug || ''} />;
}

function GuestListenPageRoute() {
  const { token } = useParams();
  return <ListenPage guestToken={token || ''} />;
}

// Admin panel
function AdminPanelPage() {
  const navigate = useNavigate();
  return <AdminPanel onBack={() => navigate('/')} />;
}

function ClassPreviewPage() {
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const [pack, setPack] = useState(null);
  const [status, setStatus] = useState('loading');
  const [retry, setRetry] = useState(0);
  const packId = searchParams.get('pack');
  const isAdmin = ['admin', 'ltbb_admin'].includes(user?.role);

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) { setStatus('admin'); return; }
    if (!packId) { setStatus('not-found'); return; }
    let active = true;
    setStatus('loading'); setPack(null);
    getClassPack(packId).then((result) => {
      if (active) { setPack(result); setStatus('ready'); }
    }).catch((error) => {
      if (!active) return;
      if ([401, 403].includes(error.status)) setStatus('admin');
      else if (error.status === 404) setStatus('not-found');
      else setStatus('error');
    });
    return () => { active = false; };
  }, [isAdmin, loading, packId, retry]);

  if (loading || status === 'loading') return <div className="class-preview-gate" data-skin="playground" role="status">Loading class preview...</div>;
  if (status === 'admin') return <div className="class-preview-gate" data-skin="playground">Admin access required</div>;
  if (status === 'not-found') return <div className="class-preview-gate" data-skin="playground">Pack not found</div>;
  if (status === 'error') return <div className="class-preview-gate" data-skin="playground"><span>Couldn&apos;t load this pack</span><button onClick={() => setRetry((value) => value + 1)}>Retry</button></div>;
  return pack ? <BeatArranger classPack={pack} canToggleClassFocus /> : null;
}

function KidSeatPage() {
  const [active, setActive] = useState(null);
  const [message, setMessage] = useState('');
  const [joinKey, setJoinKey] = useState(0);
  // A new seat card scanned into this tab: restart sign-in so JoinPage consumes the fragment.
  useQrHashChange(() => { setActive(null); setMessage(''); setJoinKey((value) => value + 1); });
  if (!active) return <JoinPage
    key={joinKey}
    signedOutMessage={message}
    onSession={(session, token) => { setMessage(''); setActive({ session, token }); }}
  />;
  if (active.session.mode === 'home') return <HomeSeatPage
    session={active.session}
    token={active.token}
    onExit={(nextMessage) => { setActive(null); setMessage(nextMessage); setJoinKey((value) => value + 1); }}
  />;
  return <SeatSessionPage
    initialSession={active.session}
    token={active.token}
    onExit={(nextMessage) => { setActive(null); setMessage(nextMessage); setJoinKey((value) => value + 1); }}
    renderEditor={({ key, ...props }) => <BeatArranger key={key} {...props} />}
  />;
}

// App with routing
function App() {
  // Render the token landing page outside the main application router so a
  // validated PUBLIC_FRONTEND_URL base path works as well as a root deployment.
  if (window.location.pathname.endsWith('/reset-password')) return <ResetPasswordPage />;
  const gated = currentScreenIsPhone();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/family" element={<FamilySignupPage />} />
        <Route path="/judge" element={<JudgePage />} />
        <Route path="/expo/listen/:token" element={<ExpoListenRoute />} />
        <Route path="/expo/:eventKey" element={<ExpoStationRoute />} />
        <Route path="/workshop" element={gated ? <PhoneGate /> : <WorkshopPage />} />
        <Route path="/facilitator" element={<FacilitatorPage />} />
        <Route path="/facilitator/class/:programId" element={<ClassProgramLiveView />} />
        <Route path="/facilitator/class/:programId/showcase" element={<ShowcasePlaylist />} />
        <Route path="/facilitator/class/:programId/projector" element={<ClassProgramProjector />} />
        <Route path="/facilitator/class/:programId/battle/:battleId/screen" element={<BattleProjector />} />
        <Route path="/dashboard" element={gated ? <PhoneGate /> : <DashboardPage />} />
        <Route path="/listen/:slug" element={<ListenPageRoute />} />
        <Route path="/listen/g/:token" element={<GuestListenPageRoute />} />
        <Route path="/admin/beat-arranger" element={<AdminPanelPage />} />
        <Route path="/class" element={gated ? <PhoneGate /> : <ClassPreviewPage />} />
        <Route path="/join" element={<KidSeatPage />} />
        <Route path="/take/:token" element={<TakeHomePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="*" element={gated ? <PhoneGate /> : <BeatArranger />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

function ExpoStationRoute() {
  const { eventKey } = useParams();
  return <ExpoStation key={eventKey} eventKey={eventKey} renderEditor={({ key, ...props }) => <BeatArranger key={key} {...props} />} />;
}
function ExpoListenRoute() {
  const { token } = useParams();
  return <ExpoListen key={token} token={token} />;
}
